import { connect } from "react-redux";
import { useHistory } from "../../../../../router/useHistory";
import I18n, { i18n } from "../../../languageSelector";
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import PlanDialog from "../../../../../components/dialogs/PlanDialog";
import SubscriptionCustomToast from "../../../../../components/dialogs/SubscriptionCustomToast";
import {
  getAllInvoices,
  getSubscriptionByCompanyId,
  updateSubscriptionData,
} from "../../../../../redux/reducers/subscription/subscription.reducer";
import { getSession } from "../../../../../redux/reducers/authentication/authentication";
import {
  createUser,
  getAllUsers,
  updateUserById,
} from "../../../../../redux/reducers/user/user.reducer";
import { useParams } from "react-router-dom";
import { getInvoices } from "../../../../../redux/reducers/invoices/invoices.reducer";
import {
  getCard,
  getCompanyById,
} from "../../../../../redux/reducers/company/company.reducer";
import {
  checkIsSubscriptionExpired,
  checkIsSubscriptionRunning,
  checkIsTrialCancelled,
  checkIsTrialExpired,
  checkIsTrialRunning,
  checkIsTrialSubscriptionRunning,
} from "../../../../../config/constants";
import { Button } from "../../../../../components/button/Button";
import { getBillingCycleDates } from "../../../../../helper/helperFunctions";
import moment from "dayjs";

// ─── Helper: safe number conversion (never returns NaN) ───────────────────────
const safeNum = (val, fallback = 0) => {
  const n = Number(val);
  return isNaN(n) || val === null || val === undefined ? fallback : n;
};

// ─── Failure reason normalization ─────────────────────────────────────────────
// Backend SHOULD stamp `i18nKey` on every payment failure (via the moyasar-
// error-map utility), but older webhook payloads, mid-deploy races, or
// Moyasar passthroughs occasionally surface raw uppercase strings like
// "DECLINED" or "DO_NOT_HONOR". Per ticket: "Remove reliance on vague
// messages such as 'DECLINED' without contextual guidance."
//
// This map normalizes those raw forms to the existing
// Subscriptions.PaymentFailure* i18n keys that already have contextual
// EN+AR translations. Lookup is case-insensitive (we uppercase the input).
// Unmapped strings fall through to the existing renderer (which falls
// back to the raw string) — no regression.
const FAILURE_REASON_NORMALIZATION = {
  DECLINED: "PaymentFailureCardDeclined",
  CARD_DECLINED: "PaymentFailureCardDeclined",
  DO_NOT_HONOR: "PaymentFailureDoNotHonor",
  INSUFFICIENT_FUNDS: "PaymentFailureInsufficientFunds",
  EXPIRED_CARD: "PaymentFailureExpiredCard",
  INVALID_CARD_NUMBER: "PaymentFailureInvalidCardNumber",
  INVALID_CVC: "PaymentFailureInvalidCvc",
  INVALID_CVV: "PaymentFailureInvalidCvc",
  AUTHENTICATION_FAILED: "PaymentFailureAuthenticationFailed",
  AUTHENTICATION_REQUIRED: "PaymentFailureAuthenticationFailed",
  TIMEOUT: "PaymentFailureTimeout",
  GATEWAY_TIMEOUT: "PaymentFailureTimeout",
};
const normalizeFailureReason = (raw) => {
  if (typeof raw !== "string" || !raw) return raw;
  // Keys already namespaced or in PascalCase shouldn't be normalized —
  // they're already mapped to i18n entries. Only the "raw" uppercase /
  // SNAKE_CASE shapes are ambiguous and need this layer.
  if (raw.includes(".")) return raw;
  if (raw[0] === raw[0]?.toUpperCase() && !raw.includes("_") && raw !== raw.toUpperCase()) {
    return raw;
  }
  const normalized = FAILURE_REASON_NORMALIZATION[raw.toUpperCase()];
  return normalized || raw;
};

const ChargeBillingDetails = ({
  show,
  onHide,
  user,
  selectedPlan,
  updateSubscriptionData,
  subscriptionData,
  getSubscriptionByCompanyId,
  loading,
  getSession,
  isAdditionalSeatsAdd,
  billingSeats,
  billingTotalSeats,
  openBillingDetailsIfSeatsIsZero,
  createUser,
  userData,
  accessRightsValues,
  seatedType,
  getAllUsers,
  updateUserById,
  data,
  userForEdit,
  setUpdateSeatsModal,
  fromUsers,
  showSeatAddWarning,
  // initialPaymentState: "processing" | "failed" | undefined("invoice").
  // Lets the parent open this modal mid-state-machine:
  //   • "processing" + autoStartRetry → footer/recovery Retry: fire the
  //     /retry-payment call immediately, user sees Processing →
  //     Success/Failed without an Invoice Preview step.
  //   • "failed" + initialFailureReason → cron-renewal-failure display:
  //     open straight on the Payment Failed dialog showing the prior
  //     decline reason, with Retry + Close.
  initialPaymentState,
  // When true (paired with initialPaymentState="processing"),
  // programmatically fire handleRetry() once the modal is shown and the
  // 500ms confirmArmed window has elapsed.
  autoStartRetry,
  // i18n key OR raw message for the Failed-dialog reason line when the
  // modal opens directly in "failed" state (cron-failure display).
  initialFailureReason,
  // Moyasar payment id of an ALREADY in-flight /retry-payment, passed by the
  // parent ONLY when restoring a Processing dialog after the AppRoutes remount
  // cascade (built from sessionStorage `inFlowPaymentId` / the persisted
  // processingDialogActive snapshot). When set: (a) seed `currentPaymentId`
  // so the payment-status socket event resolves THIS dialog, and (b) SUPPRESS
  // the autoStartRetry effect so the restore re-attaches instead of firing a
  // SECOND /retry-payment (double-charge guard). null on a fresh retry open.
  initialPaymentId,
}) => {
  const language = user?.language;
  const history = useHistory();
  const { id } = useParams();
  const [newSelectedPlan, setNewSelectedPlan] = useState("");
  const [addPaymentModal, setAddPaymentModal] = useState(false);
  // Retained for backward-compat with branches that flip the legacy boolean,
  // but the new state machine (paymentState) is the source of truth for which
  // body view renders. Setting this true/false outside the state-machine flow
  // is harmless — the render no longer reads it for processing/failed/success.
  const [showProcessPaymentLoader, setShowProcessPaymentLoader] = useState(false);
  const [isPlanChanged, setIsPlanChanged] = useState(false);

  // ─── State machine: drives the in-place dialog content swap ─────────────────
  // Figma flow: Invoice Preview → Processing → (Failed | Success).
  // Replaces the prior empty-spinner ternary at the bottom of this file.
  // Same modal box / same dimensions — only the inner content swaps.
  const [paymentState, setPaymentState] = useState("invoice");

  // Once the user has clicked Confirm (paymentState !== "invoice"), purge the
  // `pendingInvoicePreview` sessionStorage flag immediately. The parent's
  // auto-open useEffect at company-subscription-view.jsx:303 reads that flag
  // on remount and re-opens the modal with paymentState reset to "invoice".
  // The AppRoutes remount cascade fires DURING success processing because
  // App.js's `payment_status` PAYMENT_PAID handler calls getSession(), which
  // dispatches REQUEST(GET_SESSION) → authentication.js clears `user` →
  // AppLayout swaps its conditional <WrapperContainer> JSX shape → the
  // entire AppRoutes tree unmounts → ChargeBillingDetails unmounts (losing
  // local paymentState="success") → remounts → auto-open useEffect re-fires
  // → flag is still set → Invoice Preview reappears with the user's plan
  // already charged. User clicks Confirm a second time → double charge.
  //
  // Clearing the flag at "processing" is correct: from that point on, the
  // user's intent has been captured server-side as a PendingPayment row, so
  // any subsequent remount is recovered by the post-Moyasar / socket path,
  // not by reopening the local Invoice Preview. Mirrors the
  // clearPendingInvoicePreview pattern in company-subscription-view.jsx but
  // by direct key access (the helper isn't exported across files).
  useEffect(() => {
    if (paymentState !== "invoice") {
      try {
        sessionStorage.removeItem("pendingInvoicePreview");
      } catch (_) {
        /* sessionStorage may be unavailable */
      }
    }
  }, [paymentState]);

  // i18n key OR raw string for the appended "Reason: …" line on Failed state.
  // Populated from backend response (`i18nKey`) on sync failures, from socket
  // PAYMENT_FAILED event (`data.i18nKey`) on async failures, or null when no
  // structured reason is available.
  const [failureReasonKey, setFailureReasonKey] = useState(null);
  // Current Moyasar payment id — set when the backend returns
  // `{processing:true, paymentId}` and used to (a) match payment_status socket
  // events against THIS attempt and (b) signal App.js to suppress its
  // toast+notification for the same id (so the dialog is the single source
  // of feedback for the in-flow charge).
  const [currentPaymentId, setCurrentPaymentId] = useState(null);
  // Guards the outer click handlers' `onHide()` + `history.push()` from firing
  // after `await handlePayment("Charge")`. Once the state machine engages, the
  // Close button on Success/Failed views owns those transitions. Synchronous
  // ref to win the race against the post-await microtask.
  const stateMachineActiveRef = useRef(false);
  // Re-entrancy guard for handlePayment. Confirm buttons are <div onClick>
  // (no native disabled), so a double-click would otherwise fire two
  // updateSubscriptionData dispatches. submittingRef is the synchronous
  // gate (state would race within the same tick); submitting is exposed
  // for any future button-disabled rendering.
  const submittingRef = useRef(false);
  // eslint-disable-next-line no-unused-vars
  const [submitting, setSubmitting] = useState(false);

  // "Confirm armed" gate. The modal's Confirm button is a <div onClick>,
  // and rapid modal transitions (PricingPlanDialog Continue → this
  // Invoice Preview opens at the same screen position) can let a stray
  // mouseup from the previous click land on the new modal's Confirm,
  // firing handlePayment without the user actually clicking it. Stay
  // disarmed for 500ms after the modal becomes visible so a fresh
  // user-initiated click is required.
  const [confirmArmed, setConfirmArmed] = useState(false);
  useEffect(() => {
    if (!show) {
      setConfirmArmed(false);
      return;
    }
    setConfirmArmed(false);
    const t = setTimeout(() => setConfirmArmed(true), 500);
    return () => clearTimeout(t);
  }, [show]);

  // ─── State machine reset on show ────────────────────────────────────────────
  // When the modal opens fresh, start in 'invoice' — or honor
  // initialPaymentState from the parent:
  //   • "processing" → Retry/recovery entry: skip Invoice Preview, jump
  //     straight to the in-flight retry call (autoStartRetry fires it).
  //   • "failed" → cron-failure display: open straight on the Payment
  //     Failed dialog, seeded with initialFailureReason.
  // On close, no reset needed — unmount-on-close is implied by
  // `{chargeBillingDetailsDailog && ...}` in the parent.
  useEffect(() => {
    if (!show) {
      stateMachineActiveRef.current = false;
      return;
    }
    const startState =
      initialPaymentState === "processing"
        ? "processing"
        : initialPaymentState === "failed"
        ? "failed"
        : initialPaymentState === "success"
        ? "success"
        : "invoice";
    setPaymentState(startState);
    setFailureReasonKey(startState === "failed" ? initialFailureReason || null : null);
    // Seed currentPaymentId from initialPaymentId when restoring a Processing
    // dialog for an in-flight /retry-payment after a remount — without this the
    // socket-driven transition effect (which requires currentPaymentId to match
    // the event) never resolves the restored dialog and it hangs in Processing
    // until the 60s timeout. null on a fresh open (Subscribe/Invoice/cron-Failed
    // entries), where currentPaymentId is set later by handlePayment/handleRetry.
    setCurrentPaymentId(initialPaymentId ? String(initialPaymentId) : null);
    // State machine is "active" (Close owns the exit) for processing,
    // failed, and success entries — the user must not be bounced back to
    // Invoice Preview by an outer click handler. The success-entry case
    // exists so the persisted Success dialog (after AppRoutes remount
    // cascade triggered by PAYMENT_PAID's getSession) restores correctly.
    stateMachineActiveRef.current = startState !== "invoice";
    // NOTE: `initialPaymentId` is intentionally NOT a dependency. It is read
    // ONCE here at open/mount to seed currentPaymentId for a remount-restore.
    // The parent recomputes the prop from sticky `inFlowPaymentId` on every
    // render, so a mid-flow change (null→id when handleRetry sets inFlowPaymentId)
    // must NOT re-run this effect — doing so would reset a terminal failed/success
    // view back to "processing" (clobbering the resolved state). The effect still
    // runs on the `show` transition, which is exactly when restore-seeding is
    // needed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, initialPaymentState, initialFailureReason]);

  // ─── Auto-start retry (footer / recovery Retry) ─────────────────────────────
  // Parent opens this modal with initialPaymentState="processing" +
  // autoStartRetry=true. Once `show` and `confirmArmed` are both true we
  // programmatically fire handleRetry() so the user lands directly on
  // Processing → Success/Failed without clicking anything. Ref-guarded so
  // it fires exactly once per modal-open (confirmArmed re-arms on every
  // payment_status socket transition).
  const autoStartRetryFiredRef = useRef(false);
  useEffect(() => {
    if (!show) {
      autoStartRetryFiredRef.current = false;
      return;
    }
    if (!autoStartRetry || !confirmArmed) return;
    // DOUBLE-CHARGE GUARD: when restoring a Processing dialog for a payment
    // that's ALREADY in flight (initialPaymentId set from inFlowPaymentId /
    // the persisted snapshot), do NOT fire a second /retry-payment. Mark it
    // fired and bail — currentPaymentId was seeded from initialPaymentId in the
    // reset effect, so the payment-status socket event resolves this dialog to
    // Success/Failed normally. The backend 409 "another retry in flight" guard
    // is only a backstop; it misses once the webhook flips the row to paid.
    if (initialPaymentId) {
      autoStartRetryFiredRef.current = true;
      return;
    }
    if (autoStartRetryFiredRef.current) return;
    autoStartRetryFiredRef.current = true;
    handleRetry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, autoStartRetry, confirmArmed, initialPaymentId]);

  useEffect(() => {
    if (selectedPlan) {
      setNewSelectedPlan(selectedPlan);
    }
  }, [selectedPlan]);

  // ─── Socket-driven transitions out of 'processing' ──────────────────────────
  // App.js owns the socket connection and forwards payment_status events via a
  // window CustomEvent so this module doesn't open a second connection. The
  // event handler matches against currentPaymentId so unrelated cron/renewal
  // events for other payments don't transition this dialog. PAYMENT_PAID →
  // success; PAYMENT_FAILED → failed with reason; PAYMENT_AUTHORIZED /
  // PAYMENT_CAPTURED are 3DS intermediate states and are ignored (the final
  // PAID/FAILED event always follows).
  useEffect(() => {
    if (paymentState !== "processing") return undefined;
    const handler = (evt) => {
      const data = evt?.detail || {};
      const moyasar = data.moyasarResponse || {};
      const eventPaymentId = moyasar.id || data.paymentId;
      if (!eventPaymentId || !currentPaymentId) return;
      if (String(eventPaymentId) !== String(currentPaymentId)) return;
      const eventKind = data.event || moyasar.status;
      if (eventKind === "PAYMENT_PAID" || moyasar.status === "paid") {
        if (initialPaymentState === "processing") {
          // Auto-retry / card-update recovery flow (Paused subscription →
          // Update Card → auto-charge): per the client requirement, do NOT
          // show the intermediate "Payment Successful" dialog. Auto-close the
          // Processing dialog and surface the on-page success banner instead.
          // handleClose("success") sync-writes `subscribedSuccessfullyBanner`
          // (survives the PAYMENT_PAID → getSession → AppRoutes remount via the
          // parent's mount-time initializer), fires the success toast, clears
          // the `successDialogActive` restore flag, and closes + navigates.
          // Deliberately NOT calling writeSuccessDialogFlagSync() here — that
          // would restore the success DIALOG after the remount, which is
          // exactly what this flow suppresses.
          handleClose("success");
        } else {
          // Normal Subscribe / Invoice-Preview flow: keep the explicit
          // "Payment Successful" dialog (reinstated per client direction — see
          // CLAUDE.md). Sync write BEFORE state updates — App.js's getSession()
          // in the same tick will trigger AppRoutes unmount, dropping the
          // setPaymentState commit and the deferred success write-effect.
          writeSuccessDialogFlagSync();
          setConfirmArmed(false);
          setTimeout(() => setConfirmArmed(true), 500);
          setPaymentState("success");
        }
      } else if (
        eventKind === "PAYMENT_FAILED" ||
        moyasar.status === "failed" ||
        eventKind === "payment_failed"
      ) {
        const reasonKey = data.i18nKey || moyasar?.source?.i18nKey || null;
        // SYNC write BEFORE state updates — App.js's getSession() in the
        // same tick will trigger AppRoutes unmount, dropping the
        // setPaymentState commit and the deferred write-effect. The sync
        // write survives the unmount and the post-remount opener
        // useEffect's persistedFailed branch picks it up.
        writeFailedDialogFlagSync(reasonKey);
        // The in-flight payment resolved (failed) — drop the Processing-restore
        // flag so a remount restores the Failed dialog (terminal flag wins by
        // initializer ordering) and never a stale spinner. The sync write above
        // is what actually survives; this clear is belt-and-suspenders.
        try { sessionStorage.removeItem("processingDialogActive"); } catch (_) { /* sessionStorage may be unavailable */ }
        setFailureReasonKey(reasonKey);
        setConfirmArmed(false);
        setTimeout(() => setConfirmArmed(true), 500);
        setPaymentState("failed");
      }
    };
    window.addEventListener("payment-status", handler);
    return () => window.removeEventListener("payment-status", handler);
  }, [paymentState, currentPaymentId]);

  // Persist Failed-dialog state so the parent can restore the modal after
  // the AppRoutes remount cascade fires. App.js's payment_status handler
  // calls getSession() on every PAYMENT_FAILED → REQUEST(GET_SESSION)
  // clears `user` → AppLayout JSX-shape swap → AppRoutes tree unmounts →
  // this component unmounts (paymentState lost).
  //
  // CRITICAL: The previous useEffect-based write (deferred until after
  // paymentState commits) RACED with the remount: setPaymentState("failed")
  // was queued by the socket listener, then App.js's getSession() in the
  // same tick triggered the remount cascade BEFORE the React commit, so
  // the useEffect never fired and the flag was never persisted. On
  // remount the persistedFailed branch found nothing → modal stayed
  // closed → user reported "modal closes, nothing happens" for the
  // footer-Retry → fails-again path.
  //
  // Fix: write the flag SYNCHRONOUSLY at every setPaymentState("failed")
  // call site (8 sites) via this helper, BEFORE the state update is
  // queued. The deferred useEffect below remains as a backup that
  // overwrites with the latest state-derived values if the render+commit
  // does complete — but the sync write guarantees the flag exists even
  // if the commit gets dropped by an unmount.
  const writeFailedDialogFlagSync = (reasonKey) => {
    try {
      sessionStorage.setItem(
        "failedDialogActive",
        JSON.stringify({
          failureReasonKey: reasonKey || null,
          plan: newSelectedPlan || subscriptionData?.currentPlan?.plan || null,
          timestamp: Date.now(),
        })
      );
    } catch (_) {
      /* sessionStorage may be unavailable */
    }
  };

  // Mirror of writeFailedDialogFlagSync for the success path. App.js's
  // payment_status PAYMENT_PAID handler ALSO calls getSession() → same
  // AppRoutes remount cascade. Without a sync write, the deferred
  // success-write useEffect races with the unmount and the Payment
  // Successful dialog can be missed. Sync write ensures the flag is in
  // sessionStorage before any state-commit can be dropped.
  const writeSuccessDialogFlagSync = () => {
    try {
      sessionStorage.setItem(
        "successDialogActive",
        JSON.stringify({
          plan: newSelectedPlan || subscriptionData?.currentPlan?.plan || null,
          timestamp: Date.now(),
        })
      );
    } catch (_) {
      /* sessionStorage may be unavailable */
    }
  };

  // Backup deferred write — fires AFTER paymentState commits with the
  // latest failureReasonKey/newSelectedPlan from state. Overrides the
  // sync write's local-closure values with potentially more accurate
  // state-derived ones in the happy path (no unmount). In the unmount
  // path, this effect never fires, and the sync write at the call site
  // is what survives.
  useEffect(() => {
    if (paymentState !== "failed") return;
    try {
      sessionStorage.setItem(
        "failedDialogActive",
        JSON.stringify({
          failureReasonKey: failureReasonKey || null,
          plan: newSelectedPlan || subscriptionData?.currentPlan?.plan || null,
          timestamp: Date.now(),
        })
      );
    } catch (_) {
      /* sessionStorage may be unavailable */
    }
  }, [paymentState, failureReasonKey, newSelectedPlan, subscriptionData?.currentPlan?.plan]);

  // Persist Success-dialog state so the parent can restore the modal
  // after the AppRoutes remount cascade fires. Mirrors the failed-state
  // persistence above. App.js's payment_status PAYMENT_PAID handler also
  // calls getSession() → REQUEST(GET_SESSION) clears `user` → AppLayout
  // JSX-shape swap → AppRoutes unmount → this component unmounts and
  // local paymentState="success" is lost. Without persistence the user
  // sees the Processing dialog disappear and never reaches the Success
  // confirmation — the "success state fails to appear entirely" bug
  // from ticket #2. company-subscription-view.jsx's opener useEffect
  // reads SUCCESS_DIALOG_KEY on remount and re-opens with
  // initialPaymentState="success". Cleared only on user Close.
  useEffect(() => {
    if (paymentState !== "success") return;
    try {
      sessionStorage.setItem(
        "successDialogActive",
        JSON.stringify({
          plan: newSelectedPlan || subscriptionData?.currentPlan?.plan || null,
          timestamp: Date.now(),
        })
      );
    } catch (_) {
      /* sessionStorage may be unavailable */
    }
  }, [paymentState, newSelectedPlan, subscriptionData?.currentPlan?.plan]);

  // ─── Processing timeout — bound the wait ────────────────────────────────────
  // Moyasar tokenized charges (no 3DS) typically resolve in 1-3 seconds. The
  // webhook delivery + socket round-trip adds ~100-500ms. If we hit 60s with
  // no response AND no socket event, surface a recoverable failure so the
  // user isn't stuck in a spinner forever. Reason is a dedicated i18n key the
  // user can Retry from.
  useEffect(() => {
    if (paymentState !== "processing") return undefined;
    const t = setTimeout(async () => {
      // FINAL reconciliation before ever declaring failure. Prod Moyasar
      // webhooks can land at ~60s+ (observed 62s), so the old hard "failed"
      // at 60s fired while the charge had actually succeeded + activated —
      // the reported "UI says Payment Failed but backend collected + activated"
      // bug. Do ONE last backend check (the source of truth): if the
      // subscription is genuinely active, show Success, never a false failure.
      try {
        const resp = await getSubscriptionByCompanyId(user?.company?.id);
        const sub = resp?.value?.data;
        if (sub?.subscriptionInfo?.isSubscribed === true && sub?.currentPlan?.status === "Running") {
          writeSuccessDialogFlagSync();
          if (initialPaymentState === "processing") {
            handleClose("success");
          } else {
            setConfirmArmed(false);
            setTimeout(() => setConfirmArmed(true), 500);
            setPaymentState("success");
          }
          return;
        }
      } catch (_) {
        /* fall through to the timeout-failed surface below */
      }
      // Sync write before state updates — see writeFailedDialogFlagSync
      // comment block. Even though the timeout typically does NOT
      // race with an AppRoutes remount (no socket event firing
      // getSession), we still write synchronously for uniformity and
      // defense-in-depth.
      writeFailedDialogFlagSync("Payment.TimeoutReason");
      // The payment is resolved (timed out → failed): clear the in-flight
      // marker so a later FRESH retry isn't mistaken for an in-flight one and
      // suppressed by the `initialPaymentId` autoStartRetry guard. Safe to clear
      // in the dialog here (unlike the socket branches) because the timeout
      // fires only when NO socket event arrived — there's no concurrent App.js
      // dedupe read to race. Also drop the Processing-restore flag.
      try { sessionStorage.removeItem("inFlowPaymentId"); } catch (_) { /* sessionStorage may be unavailable */ }
      try { sessionStorage.removeItem("processingDialogActive"); } catch (_) { /* sessionStorage may be unavailable */ }
      setFailureReasonKey("Payment.TimeoutReason");
      // Disarm + re-arm to match the socket-driven transition pattern. The
      // Retry/Close buttons are about to mount; the 500ms window prevents
      // an inertia mouseup from the Confirm button (long since unmounted,
      // but the event-loop microtask order isn't guaranteed) from
      // accidentally firing Retry/Close.
      setConfirmArmed(false);
      setTimeout(() => setConfirmArmed(true), 500);
      setPaymentState("failed");
    }, 95000);
    return () => clearTimeout(t);
  }, [paymentState, currentPaymentId]);

  // ─── Deterministic outcome poll (the backend is the source of truth) ────────
  // The socket payment_status event is unreliable after the cross-domain Moyasar
  // reload (and prod webhooks can take 60s+ to deliver), so the dialog must NOT
  // depend on it to decide the verdict — that desynced the UI from the backend
  // (UI "Payment Failed" while the charge actually succeeded + activated; or a
  // spinner stuck forever). While a charge is in flight we POLL the subscription
  // itself: Running + isSubscribed → Success; Paused with a failure newer than
  // this attempt → Failed. Whichever of {poll, socket, timeout} resolves first
  // wins — all guard on paymentState === "processing" and this effect cancels on
  // any state change. The poll only READS (getSubscriptionByCompanyId); it never
  // re-charges, so there is no double-charge risk.
  useEffect(() => {
    if (paymentState !== "processing") return undefined;
    if (!currentPaymentId) return undefined; // only once a charge is in flight
    const companyId = user?.company?.id;
    if (!companyId) return undefined;
    let cancelled = false;
    let timer = null;
    let attempts = 0;
    const startedAt = Date.now();
    const resolveSuccess = () => {
      writeSuccessDialogFlagSync();
      if (initialPaymentState === "processing") {
        handleClose("success");
      } else {
        setConfirmArmed(false);
        setTimeout(() => setConfirmArmed(true), 500);
        setPaymentState("success");
      }
    };
    const resolveFailed = (reasonKey) => {
      writeFailedDialogFlagSync(reasonKey || null);
      setFailureReasonKey(reasonKey || null);
      try { sessionStorage.removeItem("inFlowPaymentId"); } catch (_) { /* sessionStorage may be unavailable */ }
      try { sessionStorage.removeItem("processingDialogActive"); } catch (_) { /* sessionStorage may be unavailable */ }
      setConfirmArmed(false);
      setTimeout(() => setConfirmArmed(true), 500);
      setPaymentState("failed");
    };
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const resp = await getSubscriptionByCompanyId(companyId);
        const sub = resp?.value?.data;
        if (!cancelled && sub) {
          const active =
            sub?.subscriptionInfo?.isSubscribed === true &&
            sub?.currentPlan?.status === "Running";
          const lpf = sub?.lastPaymentFailure;
          const failedAtMs = lpf?.failedAt ? new Date(lpf.failedAt).getTime() : 0;
          // Only a failure stamped at/after THIS attempt counts — the pre-retry
          // failure (the one that paused the sub) must not be read as the result.
          const freshFailure =
            sub?.currentPlan?.status === "Paused" && failedAtMs >= startedAt - 5000;
          if (active) { resolveSuccess(); return; }
          if (freshFailure) { resolveFailed(lpf?.i18nKey || lpf?.message || null); return; }
        }
      } catch (_) {
        /* transient — keep polling */
      }
      if (!cancelled && attempts < 30) timer = setTimeout(poll, 3000);
    };
    timer = setTimeout(poll, 3000);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentState, currentPaymentId]);

  // ─── Safe base values ────────────────────────────────────────────────────────
  const safeBillingSeats = safeNum(billingSeats, 0);
  const safeBillingTotalSeats = safeNum(billingTotalSeats, 0);
  const safeSeatsTotal = safeNum(subscriptionData?.seats?.total, 0);
  const safeSeatsUsed = safeNum(subscriptionData?.seats?.used, 0);
  const safeSeatsAvailable = safeNum(subscriptionData?.seats?.available, 0);

  // ─── Effective plan ──────────────────────────────────────────────────────────
  const effectivePlan = newSelectedPlan
    ? newSelectedPlan
    : subscriptionData?.currentPlan?.plan;

  const parsedBillingDetails = subscriptionData?.lastSubscriptionData;

  const effectivePlanForCalc = newSelectedPlan
    ? newSelectedPlan
    : parsedBillingDetails?.plan;

  // ─── Subscription end / hours remaining ──────────────────────────────────────
  let subscriptionEndDate = new Date(subscriptionData?.subscriptionEndedAt);
  let currentDate = new Date();
  let timeDifference = subscriptionEndDate.getTime() - currentDate.getTime();
  let hoursRemaining = timeDifference / (1000 * 3600);

  // Guard: if subscriptionEndedAt is missing/invalid hoursRemaining becomes NaN
  if (isNaN(hoursRemaining)) {
    hoursRemaining = 0;
  }

  // ─── Cost per seat ────────────────────────────────────────────────────────────
  const costPerSeatPerMonth = effectivePlan === "Monthly Subscription" ? 79 : 790;
  const monthlyYearly = effectivePlan === "Monthly Subscription" ? 30 : 365;

  // When Trial is running, subscriptionEndedAt = trial end (not a paid sub end),
  // so hoursRemaining will be 0 or negative — use flat price instead of prorating.
  const isTrialContext =
    subscriptionData?.currentPlan?.plan === "Trial" ||
    subscriptionData?.currentPlan?.status === "Trial is running";

  // Guard division by zero (monthlyYearly * 24 is always > 0, but guard anyway)
  let proratedCostPerSeat = 0;
  // No active billing cycle to prorate against: trial, expired, paused, free
  // status, or subscriptionEndedAt already in the past (hoursRemaining <= 0).
  // In those cases the invoice preview must show the full plan price per seat,
  // not 0.00 — this is the state when a user clicks Update Plan on an expired
  // subscription and picks new seats: total uses the full price (planPrice)
  // so the per-seat display has to match.
  const hasActiveBillingCycle =
    !isTrialContext &&
    hoursRemaining > 0 &&
    subscriptionData?.currentPlan?.status !== "Expired" &&
    subscriptionData?.currentPlan?.status !== "Paused" &&
    subscriptionData?.currentPlan?.status !== "Free";

  if (hasActiveBillingCycle && monthlyYearly > 0) {
    proratedCostPerSeat = (costPerSeatPerMonth / (monthlyYearly * 24)) * hoursRemaining;
  } else {
    proratedCostPerSeat = costPerSeatPerMonth;
  }
  if (isNaN(proratedCostPerSeat)) {
    proratedCostPerSeat = 0;
  }

  // ─── Total prorated cost (for additional seats) ───────────────────────────────
  // For Trial: billingSeats is 0/undefined, use total seats × flat price
  const seatsForTotalCost = isTrialContext
    ? (safeBillingTotalSeats > 0 ? safeBillingTotalSeats : safeSeatsTotal > 0 ? safeSeatsTotal : safeSeatsUsed)
    : safeBillingSeats;

  let totalCostBefore = seatsForTotalCost * proratedCostPerSeat;
  if (isNaN(totalCostBefore)) {
    totalCostBefore = 0;
  }

  const applyTaxRate = subscriptionData?.applyTax ? 0.15 : 0;
  let proratedSeatTax = totalCostBefore * applyTaxRate;
  if (isNaN(proratedSeatTax)) {
    proratedSeatTax = 0;
  }

  let totalCost = proratedSeatTax + totalCostBefore;
  if (isNaN(totalCost)) {
    totalCost = 0;
  }



  useEffect(() => {
    getSubscriptionByCompanyId(user?.company?.id);
  }, [show, user, getSubscriptionByCompanyId]);

  // ─── usedSeatCalculation ─────────────────────────────────────────────────────
  let usedSeatCalculation;

  const planPrice = (plan) => (plan === "Monthly Subscription" ? 79 : 790);

  if (
    subscriptionData?.currentPlan?.status === "Expired" &&
    billingSeats === undefined &&
    billingTotalSeats === undefined
  ) {
    const storedPlan = localStorage.getItem("newSelectedPlan");
    if (storedPlan) {
      usedSeatCalculation = planPrice(storedPlan) * safeSeatsTotal;
    }
  }

  if (subscriptionData?.currentPlan?.status === "Expired" && safeBillingSeats > 0) {
    usedSeatCalculation = planPrice(effectivePlanForCalc) * safeBillingSeats;
  }

  if (subscriptionData?.currentPlan?.status === "Expired" && safeBillingSeats < 0) {
    usedSeatCalculation = planPrice(effectivePlanForCalc) * safeBillingTotalSeats;
  }

  if (
    subscriptionData?.currentPlan?.status === "Expired" &&
    !billingSeats &&
    newSelectedPlan !== subscriptionData?.currentPlan?.plan
  ) {
    usedSeatCalculation = planPrice(effectivePlanForCalc) * safeSeatsTotal;
  }

  if (
    subscriptionData?.currentPlan?.status === "Expired" &&
    (safeBillingSeats < 0) &&
    (newSelectedPlan !== subscriptionData?.currentPlan?.plan)
  ) {
    usedSeatCalculation = planPrice(effectivePlanForCalc) * safeBillingTotalSeats;
  }

  // If user subscribes during Trial → full price × seats (NOT prorated)
  // hoursRemaining is 0 here because subscriptionEndedAt is the trial end, not a paid sub end
  // so we must use the flat plan price per seat, not proratedCostPerSeat
  if (subscriptionData?.currentPlan?.plan === "Trial" ||
    subscriptionData?.currentPlan?.status === "Trial is running") {
    // seats to bill: prefer billingTotalSeats prop, fall back to seats.total, then seats.used
    const trialSeats = safeBillingTotalSeats > 0
      ? safeBillingTotalSeats
      : safeSeatsTotal > 0
        ? safeSeatsTotal
        : safeSeatsUsed;
    usedSeatCalculation = planPrice(effectivePlanForCalc) * trialSeats;
  }

  // After trial cancel + Free plan
  if (
    subscriptionData?.userTrial?.isTrialCancelled &&
    subscriptionData?.currentPlan?.plan === "Free"
  ) {
    usedSeatCalculation = planPrice(effectivePlanForCalc) * safeBillingTotalSeats;
  }

  if (
    subscriptionData?.userTrial?.isTrialCancelled &&
    subscriptionData?.currentPlan?.plan === "Free" &&
    isAdditionalSeatsAdd === undefined
  ) {
    usedSeatCalculation = planPrice(effectivePlanForCalc) * safeSeatsUsed;
  }

  // Plan change while subscribed
  if (
    subscriptionData?.currentPlan?.plan !==
    (newSelectedPlan ? newSelectedPlan : parsedBillingDetails?.plan) &&
    subscriptionData?.subscriptionInfo?.isSubscribed === true
  ) {
    usedSeatCalculation = planPrice(effectivePlanForCalc) * safeBillingTotalSeats;
  }

  const isValidSubscription =
    (subscriptionData?.currentPlan?.status === "Running" ||
      subscriptionData?.currentPlan?.status === "Unsubscribed, subscription is running") &&
    (subscriptionData?.currentPlan?.plan === "Monthly Subscription" ||
      subscriptionData?.currentPlan?.plan === "Yearly Subscription") &&
    subscriptionData?.currentPlan?.plan ===
    (newSelectedPlan ? newSelectedPlan : parsedBillingDetails?.plan);

  // Robust active-subscription seat-upgrade detection. Independent of
  // `parsedBillingDetails` / `newSelectedPlan` state — only looks at the
  // live subscription status + the explicit positive delta passed in
  // `billingSeats` (UpdatePlan.jsx sets billingSeats = newTotal -
  // existingTotal). isValidSubscription requires
  // `currentPlan.plan === (newSelectedPlan || parsedBillingDetails?.plan)`,
  // which silently turns FALSE in two reproducible races:
  //   1. On first render, newSelectedPlan="" and the ternary falls to
  //      parsedBillingDetails?.plan (subscriptionData.lastSubscriptionData).
  //      If the post-Update redux refetch has not landed yet (or the
  //      backend Mongoose 5.7 `Object.assign + save` didn't persist
  //      lastSubscriptionData — see project_mongoose57_subdoc_save), the
  //      compared plan is undefined and the strict === fails.
  //   2. If `newSelectedPlan` somehow lands as a different string from
  //      `currentPlan.plan` during a remount cycle.
  // In both cases the fallback at line 1549 (`safeBillingTotalSeats > 0`)
  // returns the new TOTAL seat count and the Invoice Preview shows
  // "3 seats × SAR 19.50 = SAR 19.50" — the arithmetic no longer
  // multiplies out. This flag never depends on the lastSubscriptionData
  // shape, so it's safe to use as the primary discriminator for the
  // active seat-upgrade case across both Monthly and Yearly plans.
  const isActiveSeatUpgrade =
    (subscriptionData?.currentPlan?.status === "Running" ||
      subscriptionData?.currentPlan?.status === "Unsubscribed, subscription is running") &&
    (subscriptionData?.currentPlan?.plan === "Monthly Subscription" ||
      subscriptionData?.currentPlan?.plan === "Yearly Subscription") &&
    safeBillingSeats > 0 &&
    (!newSelectedPlan || newSelectedPlan === subscriptionData?.currentPlan?.plan);

  if (isValidSubscription && safeBillingSeats > 0) {
    usedSeatCalculation = planPrice(effectivePlanForCalc) * safeBillingSeats;
  } else if (isValidSubscription && safeBillingSeats <= 0) {
    usedSeatCalculation = planPrice(effectivePlanForCalc) * safeBillingTotalSeats;
  } else if (isActiveSeatUpgrade) {
    // Defense in depth: when isValidSubscription failed because of a
    // stale parsedBillingDetails or unresolved newSelectedPlan but the
    // user IS clearly performing an active seat upgrade (positive delta,
    // unchanged plan), charge against the delta — not the new total.
    usedSeatCalculation = planPrice(effectivePlanForCalc) * safeBillingSeats;
  }

  if (subscriptionData?.currentPlan?.status === "Paused") {
    if (safeBillingSeats && safeBillingTotalSeats) {
      usedSeatCalculation = planPrice(effectivePlanForCalc) * safeBillingTotalSeats;
    } else {
      usedSeatCalculation = planPrice(effectivePlanForCalc) * safeSeatsTotal;
    }
  }

  // Final NaN guard on usedSeatCalculation
  if (usedSeatCalculation === undefined || isNaN(usedSeatCalculation)) {
    usedSeatCalculation = 0;
  }

  let taxOnUsedSeatCalculation = usedSeatCalculation * applyTaxRate;
  if (isNaN(taxOnUsedSeatCalculation)) {
    taxOnUsedSeatCalculation = 0;
  }

  let totalAmount = usedSeatCalculation + taxOnUsedSeatCalculation;
  if (isNaN(totalAmount)) {
    totalAmount = 0;
  }



  // ─── tax for plan-change-only scenario ───────────────────────────────────────
  let tax =
    newSelectedPlan === "Monthly Subscription"
      ? 79 * safeSeatsTotal * applyTaxRate
      : 790 * safeSeatsTotal * applyTaxRate;
  if (isNaN(tax)) {
    tax = 0;
  }

  // ─── Shared lastAdditionalseats payload ──────────────────────────────────────
  const lastAdditionalseats = {
    seats: safeBillingSeats < 0 ? safeBillingTotalSeats : safeBillingSeats,
    seatsBill: totalCost,
    lastPerSeatBill: proratedCostPerSeat,
  };


  // ─── Subscription data payloads ──────────────────────────────────────────────

  // Discriminator: is the user currently in an active trial?
  // Only a running trial (started AND not cancelled AND not expired) defers
  // payment to trialEndCronJob and keeps status="Trial is running". Every
  // other subscribe path — post-trial, fresh signup from Free, cancelled
  // trial, expired subscription — must charge immediately so the webhook
  // invoice gate matches and no duplicate cron charge can race.
  const isMidTrialSubscribe =
    subscriptionData?.userTrial?.isTrialStarted === true &&
    subscriptionData?.userTrial?.isTrialCancelled === false &&
    subscriptionData?.userTrial?.isTrialExpired === false;
  const isPostTrialSubscribe = !isMidTrialSubscribe;

  const endedUserTrial = {
    isTrialCancelled: true,
    isTrialExpired: true,
    isTrialRunning: false,
    isTrialStarted: false,
  };

  const activeUserTrialPassthrough = {
    isTrialCancelled: subscriptionData?.userTrial?.isTrialCancelled,
    isTrialExpired: subscriptionData?.userTrial?.isTrialExpired,
    isTrialRunning: checkIsTrialRunning(subscriptionData),
    isTrialStarted: subscriptionData?.userTrial?.isTrialStarted,
  };

  const addPaymentModalData = {
    title: i18n("Subscriptions.AddPaymentMethod"),
    text_one: (
      <I18n lng="Subscriptions.YouMustAddPaymentMethodToCompleteTheProcess" />
    ),
    actionButtonText: i18n("Subscriptions.AddPaymentMethod"),
    closeButtonText: <I18n lng="global.Close" />,
  };

  useEffect(() => {
    if (
      subscriptionData?.subscriptionInfo?.isSubscribed === true &&
      (newSelectedPlan ? newSelectedPlan : parsedBillingDetails?.plan) &&
      subscriptionData?.currentPlan?.plan
    ) {
      if (
        (newSelectedPlan ? newSelectedPlan : parsedBillingDetails?.plan) !==
        subscriptionData?.currentPlan?.plan
      ) {
        setIsPlanChanged(true);
      } else {
        setIsPlanChanged(false);
      }
    }
    // eslint-disable-next-line
  }, [show]);

  const subscriptionDataToSubscribe = {
    currentPlan: {
      plan:
        effectivePlanForCalc === "Monthly Subscription"
          ? "Monthly Subscription"
          : "Yearly Subscription",
      status: isPostTrialSubscribe ? "Running" : "Trial is running",
      seats: safeSeatsTotal,
      price: effectivePlanForCalc === "Monthly Subscription" ? 79 : 790,
    },
    userTrial: isPostTrialSubscribe ? endedUserTrial : activeUserTrialPassthrough,
    seats: {
      used: safeSeatsUsed,
      available: safeSeatsTotal - safeSeatsUsed,
      total: safeSeatsTotal,
    },
    subscriptionInfo: {
      isSubscribed: true,
      isSubscribedExpired: false,
      isUnsubscribed: false,
      isSubscribeResumed: false,
    },
    nextUpdatedPlan:
      effectivePlanForCalc === "Monthly Subscription"
        ? "Monthly Subscription"
        : "Yearly Subscription",
    isPlanChanged: isPlanChanged,
    lastAdditionalseats,
  };

  const subscriptionDataThatSubscribeAfterBackToFreePlanAndAgainSubscribe = {
    currentPlan: {
      plan:
        effectivePlanForCalc === "Monthly Subscription"
          ? "Monthly Subscription"
          : "Yearly Subscription",
      status: isPostTrialSubscribe ? "Running" : "Trial is running",
      seats: safeNum(parsedBillingDetails?.seats, safeSeatsUsed),
      price: effectivePlanForCalc === "Monthly Subscription" ? 79 : 790,
    },
    userTrial: isPostTrialSubscribe ? endedUserTrial : activeUserTrialPassthrough,
    seats: {
      used: safeSeatsUsed,
      available: parsedBillingDetails?.seats
        ? safeNum(parsedBillingDetails.seats) - safeSeatsUsed
        : 0,
      total: safeNum(parsedBillingDetails?.seats, safeSeatsUsed),
    },
    subscriptionInfo: {
      isSubscribed: true,
      isSubscribedExpired: false,
      isUnsubscribed: false,
      isSubscribeResumed: false,
    },
    nextUpdatedPlan:
      effectivePlanForCalc === "Monthly Subscription"
        ? "Monthly Subscription"
        : "Yearly Subscription",
    isPlanChanged: isPlanChanged,
    lastAdditionalseats,
  };

  const subscriptionDataToSubscribeAfterCancelTrial = {
    currentPlan: {
      plan:
        effectivePlanForCalc === "Monthly Subscription"
          ? "Monthly Subscription"
          : "Yearly Subscription",
      status: isPostTrialSubscribe ? "Running" : "Trial is running",
      seats: safeNum(parsedBillingDetails?.seats, safeSeatsUsed),
      price: effectivePlanForCalc === "Monthly Subscription" ? 79 : 790,
    },
    userTrial: isPostTrialSubscribe ? endedUserTrial : activeUserTrialPassthrough,
    seats: {
      used: safeSeatsUsed,
      available: parsedBillingDetails?.seats
        ? safeNum(parsedBillingDetails.seats) - safeSeatsUsed
        : 0,
      total: safeNum(parsedBillingDetails?.seats, safeSeatsUsed),
    },
    subscriptionInfo: {
      isSubscribed: true,
      isSubscribedExpired: false,
      isUnsubscribed: false,
      isSubscribeResumed: false,
    },
    nextUpdatedPlan:
      effectivePlanForCalc === "Monthly Subscription"
        ? "Monthly Subscription"
        : "Yearly Subscription",
    isPlanChanged: isPlanChanged,
    lastAdditionalseats,
  };

  const subscriptionDataThatOnlyChangeFromMonthlyToYearly = {
    currentPlan: {
      plan:
        newSelectedPlan === "Monthly Subscription"
          ? "Monthly Subscription"
          : "Yearly Subscription",
      status: subscriptionData?.currentPlan?.status,
      seats: safeNum(subscriptionData?.currentPlan?.seats, 0),
      price: newSelectedPlan === "Monthly Subscription" ? 79 : 790,
    },
    seats: {
      used: safeSeatsUsed,
      available: safeSeatsAvailable,
      total: safeSeatsTotal,
    },
    subscriptionInfo: {
      isSubscribed: subscriptionData?.subscriptionInfo?.isSubscribed,
      isSubscribedExpired: subscriptionData?.subscriptionInfo?.isSubscribedExpired,
      isUnsubscribed: subscriptionData?.subscriptionInfo?.isUnsubscribed,
      isSubscribeResumed: false,
    },
    nextUpdatedPlan:
      newSelectedPlan === "Monthly Subscription"
        ? "Monthly Subscription"
        : "Yearly Subscription",
    isPlanChanged: isPlanChanged,
    lastAdditionalseats: {
      seats: safeSeatsTotal,
      seatsBill:
        newSelectedPlan === "Monthly Subscription"
          ? 79 * safeSeatsTotal + tax
          : safeSeatsTotal * 790 + tax,
      lastPerSeatBill: proratedCostPerSeat,
    },
  };

  const updatingSubscriptionData = {
    currentPlan: {
      plan:
        newSelectedPlan === "Monthly Subscription"
          ? "Monthly Subscription"
          : "Yearly Subscription",
      status:
        subscriptionData?.userTrial?.isTrialCancelled === false &&
          subscriptionData?.userTrial?.isTrialExpired === false
          ? "Trial is running"
          : "Running",
      seats: safeBillingTotalSeats,
      price: newSelectedPlan === "Monthly Subscription" ? 79 : 790,
    },
    seats: {
      used: safeSeatsUsed,
      available: safeBillingTotalSeats - safeSeatsUsed,
      total: safeBillingTotalSeats,
    },
    subscriptionInfo: {
      isSubscribed: true,
      isSubscribedExpired: false,
      isUnsubscribed: false,
      isSubscribeResumed: false,
    },
    nextUpdatedPlan:
      newSelectedPlan === "Monthly Subscription"
        ? "Monthly Subscription"
        : "Yearly Subscription",
    isPlanChanged: isPlanChanged,
    lastAdditionalseats,
  };

  // const updatingSubscriptionDataForPaused = {
  //   currentPlan: {
  //     plan:
  //       newSelectedPlan === "Monthly Subscription"
  //         ? "Monthly Subscription"
  //         : "Yearly Subscription",
  //     status: "Paused",
  //     seats: safeBillingTotalSeats,
  //     price: newSelectedPlan === "Monthly Subscription" ? 79 : 790,
  //   },
  //   seats: {
  //     used: safeSeatsUsed,
  //     available: safeBillingTotalSeats - safeSeatsUsed,
  //     total: safeBillingTotalSeats,
  //   },
  //   subscriptionInfo: {
  //     isSubscribed: true,
  //     isSubscribedExpired: false,
  //     isUnsubscribed: false,
  //     isSubscribeResumed: false,
  //   },
  //   nextUpdatedPlan:
  //     newSelectedPlan === "Monthly Subscription"
  //       ? "Monthly Subscription"
  //       : "Yearly Subscription",
  //   isPlanChanged: isPlanChanged,
  //   lastAdditionalseats,
  // };

  const updatingSubscriptionDataFromPausedToRunning = {
    currentPlan: {
      plan:
        newSelectedPlan === "Monthly Subscription"
          ? "Monthly Subscription"
          : "Yearly Subscription",
      status: "Running",
      seats: safeBillingTotalSeats,
      price: newSelectedPlan === "Monthly Subscription" ? 79 : 790,
    },
    seats: {
      used: safeSeatsUsed,
      available: safeBillingTotalSeats - safeSeatsUsed,
      total: safeBillingTotalSeats,
    },
    subscriptionInfo: {
      isSubscribed: true,
      isSubscribedExpired: false,
      isUnsubscribed: false,
      isSubscribeResumed: false,
    },
    nextUpdatedPlan:
      newSelectedPlan === "Monthly Subscription"
        ? "Monthly Subscription"
        : "Yearly Subscription",
    isPlanChanged: isPlanChanged,
    lastAdditionalseats,
  };

  const updatingSubscriptionDataWhileUnsubscribe = {
    currentPlan: {
      plan: newSelectedPlan
        ? newSelectedPlan
        : subscriptionData?.currentPlan?.plan,
      status:
        selectedPlan !== subscriptionData?.currentPlan?.plan
          ? "Running"
          : subscriptionData?.currentPlan?.status,
      seats: safeBillingTotalSeats,
      price: newSelectedPlan === "Monthly Subscription" ? 79 : 790,
    },
    seats: {
      used: safeSeatsUsed,
      available: safeSeatsAvailable + safeBillingSeats,
      total: safeBillingTotalSeats,
    },
    subscriptionInfo: {
      isSubscribed: true,
      isSubscribedExpired: false,
      isUnsubscribed:
        selectedPlan !== subscriptionData?.currentPlan?.plan ? false : true,
      isSubscribeResumed: false,
    },
    nextUpdatedPlan:
      newSelectedPlan === "Monthly Subscription"
        ? "Monthly Subscription"
        : "Yearly Subscription",
    isPlanChanged: isPlanChanged,
    lastAdditionalseats,
  };

  const chargeUserIfSeatsAreNotAvailableWhileSubscriptionRunning = {
    currentPlan: {
      plan:
        newSelectedPlan === "Monthly Subscription"
          ? "Monthly Subscription"
          : "Yearly Subscription",
      status:
        subscriptionData?.userTrial?.isTrialCancelled === false &&
          subscriptionData?.userTrial?.isTrialExpired === false
          ? "Trial is running"
          : "Running",
      seats: safeNum(subscriptionData?.currentPlan?.seats, 0) + 1,
      price: newSelectedPlan === "Monthly Subscription" ? 79 : 790,
    },
    seats: {
      used: safeSeatsUsed + 1,
      available: 0,
      total: safeSeatsTotal + 1,
    },
    subscriptionInfo: {
      isSubscribed: true,
      isSubscribedExpired: false,
      isUnsubscribed: false,
      isSubscribeResumed: false,
    },
    nextUpdatedPlan:
      newSelectedPlan === "Monthly Subscription"
        ? "Monthly Subscription"
        : "Yearly Subscription",
    isPlanChanged: isPlanChanged,
    lastAdditionalseats,
  };

  const chargeUserIfSeatsAreNotAvailableWhileUnSubscription = {
    currentPlan: {
      plan: subscriptionData?.currentPlan?.plan,
      status: subscriptionData?.currentPlan?.status,
      seats: safeNum(subscriptionData?.currentPlan?.seats, 0) + 1,
      price: newSelectedPlan === "Monthly Subscription" ? 79 : 790,
    },
    seats: {
      used: safeSeatsUsed + 1,
      available: 0,
      total: safeSeatsTotal + 1,
    },
    subscriptionInfo: {
      isSubscribed: true,
      isSubscribedExpired: false,
      isUnsubscribed: true,
      isSubscribeResumed: false,
    },
    nextUpdatedPlan:
      newSelectedPlan === "Monthly Subscription"
        ? "Monthly Subscription"
        : "Yearly Subscription",
    isPlanChanged: isPlanChanged,
    lastAdditionalseats,
  };

  const updateSubscriptionAndSubscriptionIsExpired = {
    currentPlan: {
      plan:
        newSelectedPlan === "Monthly Subscription"
          ? "Monthly Subscription"
          : "Yearly Subscription",
      status: "Expired",
      seats: safeBillingTotalSeats,
      price: newSelectedPlan === "Monthly Subscription" ? 79 : 790,
    },
    seats: {
      used: safeSeatsUsed,
      available: safeSeatsAvailable + safeBillingSeats,
      total: safeBillingTotalSeats,
    },
    subscriptionInfo: {
      isSubscribed: true,
      isSubscribedExpired: true,
      isUnsubscribed: true,
      isSubscribeResumed: false,
    },
    nextUpdatedPlan:
      newSelectedPlan === "Monthly Subscription"
        ? "Monthly Subscription"
        : "Yearly Subscription",
    isPlanChanged: isPlanChanged,
    lastAdditionalseats,
  };

  const updateSubscriptionThatSubscriptionIsAlreadyExpired = {
    currentPlan: {
      plan:
        newSelectedPlan === "Monthly Subscription"
          ? "Monthly Subscription"
          : "Yearly Subscription",
      status: "Running",
      seats: safeNum(subscriptionData?.currentPlan?.seats, 0),
      price: newSelectedPlan === "Monthly Subscription" ? 79 : 790,
    },
    seats: {
      used: safeSeatsUsed,
      available: safeSeatsAvailable,
      total: safeSeatsTotal,
    },
    subscriptionInfo: {
      isSubscribed: true,
      isSubscribedExpired: false,
      isUnsubscribed: false,
      isSubscribeResumed: false,
    },
    nextUpdatedPlan:
      newSelectedPlan === "Monthly Subscription"
        ? "Monthly Subscription"
        : "Yearly Subscription",
    isPlanChanged: isPlanChanged,
    lastAdditionalseats,
  };

  // const updateSubscriptionDataForPaused = {
  //   currentPlan: {
  //     plan: subscriptionData?.currentPlan?.plan,
  //     status: "Paused",
  //     seats: safeBillingTotalSeats,
  //     price: newSelectedPlan === "Monthly Subscription" ? 79 : 790,
  //   },
  //   seats: {
  //     used: safeSeatsUsed,
  //     available: safeSeatsAvailable + safeBillingSeats,
  //     total: safeBillingTotalSeats,
  //   },
  // };

  // ─── seatsForLastSubscription ─────────────────────────────────────────────────
  const seatsForLastSubscription = safeBillingTotalSeats;

  // const updateLastSubscriptionDataForAutoInvoice = {
  //   lastSubscriptionData: {
  //     plan: selectedPlan,
  //     seats: seatsForLastSubscription,
  //     seatsPrice: Number(totalCostBefore?.toFixed(2)),
  //     taxes: Number(proratedSeatTax?.toFixed(2)),
  //     total: Number(totalCost?.toFixed(2)),
  //     todaysTotal: 0,
  //     proratedCostPerSeat: proratedCostPerSeat,
  //   },
  // };

  // ─── handlePayment ────────────────────────────────────────────────────────────
  // For Charge flow: drives the in-place state machine (invoice → processing
  // → success | failed). The outer click handlers in this file fire-and-
  // forget for Charge — they do NOT await this function and do NOT call
  // onHide()/history.push() afterwards (the new Close button on the Success
  // and Failed views owns those transitions). stateMachineActiveRef is set
  // synchronously so any click handler that still awaits sees it true and
  // bails before its onHide/history.push lines.
  //
  // For Update flow (no Moyasar charge — instant DB-only update): preserves
  // the prior behavior. Click handler awaits, then closes the modal directly.
  const handlePayment = async (value) => {
    // Confirm-armed gate — see useEffect on `show`. Blocks stray mouseup
    // events from the prior modal (PricingPlanDialog Continue) landing on
    // this Confirm because the new modal renders at the same screen position.
    if (!confirmArmed) return;
    // Re-entrancy guard. Confirm buttons are <div onClick> (no native disabled)
    // so a fast double-click would otherwise spawn two parallel charge calls.
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);

    const isCharge = value === "Charge";

    if (isCharge) {
      // Enter the state machine BEFORE any await so the in-flight modal swap
      // renders Processing immediately. The render gate on paymentState also
      // unmounts the Confirm button, so a stray re-click can't fire.
      stateMachineActiveRef.current = true;
      setFailureReasonKey(null);
      setCurrentPaymentId(null);
      try { sessionStorage.removeItem("inFlowPaymentId"); } catch (_) { /* sessionStorage may be unavailable */ }
      // Defensive: a Charge (Subscribe/Invoice) flow never sets
      // processingDialogActive (that flag is exclusive to the Paused-recovery
      // retry path), but clear any stale one from an abandoned recovery flow so
      // it can't restore a spurious Processing dialog over this Charge. Mirrors
      // the inFlowPaymentId clear above.
      try { sessionStorage.removeItem("processingDialogActive"); } catch (_) { /* sessionStorage may be unavailable */ }
      setPaymentState("processing");
    } else {
      // Legacy Update path — same loader gating as before the state-machine
      // refactor. Click handler awaits then calls onHide/history.push.
      setShowProcessPaymentLoader(true);
    }

    // Captures the backend response body for the FINAL `axios.patch` issued
    // by this invocation. For Charge: the dialog reads this to decide whether
    // to stay in processing (async webhook coming) or jump straight to
    // success/failed (sync recovery/decline). For Update: stays null.
    let chargeResponseBody = null;

    // Single dispatch point — replaces inline `updateSubscriptionData(...)` in
    // each branch. For Charge it bypasses redux-promise-middleware (which
    // doesn't expose the axios response directly) and calls axios.patch so we
    // can inspect `processing` / `error` / `recovered` / `paymentId`. For
    // Update it falls back to the existing redux thunk so loadingBar +
    // notification middleware semantics are preserved.
    const runUpdate = async (subId, payload, val) => {
      if (val === "Charge") {
        const resp = await axios.patch(`/admin/subscriptions/${subId}`, { ...payload, actionType: val });
        chargeResponseBody = resp?.data || null;
        return chargeResponseBody;
      }
      return updateSubscriptionData(subId, payload, val);
    };

    try {
    if (
      subscriptionData?.currentPlan?.status === "Unsubscribed, subscription is running" &&
      subscriptionData?.subscriptionInfo?.isSubscribed &&
      subscriptionData?.subscriptionInfo?.isUnsubscribed &&
      openBillingDetailsIfSeatsIsZero === undefined &&
      safeBillingTotalSeats < safeNum(subscriptionData?.currentPlan?.seats, 0)
    ) {
      await runUpdate(subscriptionData?.id, updatingSubscriptionDataWhileUnsubscribe, value);
      if (!isCharge) {
        setShowProcessPaymentLoader(false);
        getSubscriptionByCompanyId(user?.company?.id);
      }
    } else if (
      subscriptionData?.currentPlan?.status === "Unsubscribed, subscription is running" &&
      subscriptionData?.subscriptionInfo?.isSubscribed &&
      subscriptionData?.subscriptionInfo?.isUnsubscribed &&
      openBillingDetailsIfSeatsIsZero === undefined
    ) {
      await runUpdate(subscriptionData?.id, updatingSubscriptionDataWhileUnsubscribe, value);
      if (!isCharge) {
        setShowProcessPaymentLoader(false);
        getSubscriptionByCompanyId(user?.company?.id);
      }
    } else if (
      safeBillingSeats > 0 &&
      subscriptionData?.currentPlan?.status !== "Paused" &&
      openBillingDetailsIfSeatsIsZero !== true &&
      !subscriptionData?.subscriptionInfo?.isSubscribedExpired
    ) {
      await runUpdate(subscriptionData?.id, updatingSubscriptionData, value);
      if (!isCharge) {
        setShowProcessPaymentLoader(false);
        getSubscriptionByCompanyId(user?.company?.id);
      }
    } else if (
      safeBillingSeats < 0 &&
      subscriptionData?.currentPlan?.status !== "Paused" &&
      openBillingDetailsIfSeatsIsZero !== true &&
      !subscriptionData?.subscriptionInfo?.isSubscribedExpired
    ) {
      await runUpdate(subscriptionData?.id, updatingSubscriptionData, value);
      if (!isCharge) {
        setShowProcessPaymentLoader(false);
        getSubscriptionByCompanyId(user?.company?.id);
      }
    } else if (
      subscriptionData?.currentPlan?.plan === "Free" &&
      subscriptionData?.currentPlan?.status === "Free" &&
      subscriptionData?.subscriptionInfo?.isSubscribedExpired
    ) {
      await runUpdate(
        subscriptionData?.id,
        subscriptionDataThatSubscribeAfterBackToFreePlanAndAgainSubscribe,
        value
      );
    } else if (
      safeBillingSeats === 0 &&
      subscriptionData?.currentPlan?.status !== "Paused" &&
      subscriptionData?.currentPlan?.status !== "Expired" &&
      subscriptionData?.currentPlan?.status !== "Free" &&
      subscriptionData?.currentPlan?.plan !== "Trial" &&
      subscriptionData?.currentPlan?.status !== "Trial is running"
    ) {
      await runUpdate(
        subscriptionData?.id,
        subscriptionDataThatOnlyChangeFromMonthlyToYearly,
        value
      );
    } else if (
      subscriptionData?.currentPlan?.status === "Paused" &&
      subscriptionData?.subscriptionInfo?.isSubscribed
    ) {
      await runUpdate(
        subscriptionData?.id,
        updatingSubscriptionDataFromPausedToRunning,
        value
      );
    } else if (
      !id &&
      subscriptionData?.seats?.available === 0 &&
      subscriptionData?.currentPlan?.status !== "Paused" &&
      subscriptionData?.currentPlan?.status === "Running" &&
      subscriptionData?.subscriptionInfo?.isSubscribed &&
      openBillingDetailsIfSeatsIsZero &&
      userData?.isUserSeated
    ) {
      const pendingUserOp = {
        action: "create",
        userData: {
          ...userData,
          isInvited: true,
          roleSetting: accessRightsValues?.value || accessRightsValues,
          managerPortal: false,
          inspectorPortal: false,
          isUserSeated: seatedType,
          roleName: accessRightsValues?.role?.originalName,
        },
      };
      await runUpdate(
        subscriptionData?.id,
        { ...chargeUserIfSeatsAreNotAvailableWhileSubscriptionRunning, pendingUserOp },
        value
      );
      getAllUsers();
    } else if (
      id &&
      subscriptionData?.seats?.available === 0 &&
      subscriptionData?.currentPlan?.status !== "Paused" &&
      subscriptionData?.currentPlan?.status === "Running" &&
      subscriptionData?.subscriptionInfo?.isSubscribed &&
      openBillingDetailsIfSeatsIsZero &&
      userData?.isUserSeated
    ) {
      const pendingUserOp = {
        action: "update",
        userId: id,
        userData: {
          ...data,
          roleSetting: accessRightsValues?.value || accessRightsValues,
          managerPortal: false,
          inspectorPortal: false,
          userInfoChange: userForEdit?.active !== data?.active,
          isUserSeated: seatedType,
          roleName: accessRightsValues?.role?.originalName,
        },
      };
      await runUpdate(
        subscriptionData?.id,
        { ...chargeUserIfSeatsAreNotAvailableWhileSubscriptionRunning, pendingUserOp },
        value
      );
      getAllUsers();
    } else if (
      !id &&
      subscriptionData?.seats?.available === 0 &&
      subscriptionData?.currentPlan?.status !== "Paused" &&
      subscriptionData?.currentPlan?.status === "Unsubscribed, subscription is running" &&
      subscriptionData?.subscriptionInfo?.isSubscribed &&
      openBillingDetailsIfSeatsIsZero &&
      userData?.isUserSeated
    ) {
      const pendingUserOp = {
        action: "create",
        userData: {
          ...userData,
          isInvited: true,
          roleSetting: accessRightsValues?.value || accessRightsValues,
          managerPortal: false,
          inspectorPortal: false,
          isUserSeated: seatedType,
          roleName: accessRightsValues?.role?.originalName,
        },
      };
      await runUpdate(
        subscriptionData?.id,
        { ...chargeUserIfSeatsAreNotAvailableWhileUnSubscription, pendingUserOp },
        value
      );
      getAllUsers();
    } else if (
      id &&
      subscriptionData?.seats?.available === 0 &&
      subscriptionData?.currentPlan?.status !== "Paused" &&
      subscriptionData?.currentPlan?.status === "Unsubscribed, subscription is running" &&
      subscriptionData?.subscriptionInfo?.isSubscribed &&
      openBillingDetailsIfSeatsIsZero &&
      userData?.isUserSeated
    ) {
      const pendingUserOp = {
        action: "update",
        userId: id,
        userData: {
          ...data,
          roleSetting: accessRightsValues?.value || accessRightsValues,
          managerPortal: false,
          inspectorPortal: false,
          userInfoChange: userForEdit?.active !== data?.active,
          isUserSeated: seatedType,
          roleName: accessRightsValues?.role?.originalName,
        },
      };
      await runUpdate(
        subscriptionData?.id,
        { ...chargeUserIfSeatsAreNotAvailableWhileUnSubscription, pendingUserOp },
        value
      );
      getAllUsers();
    } else if (
      subscriptionData?.currentPlan?.status === "Expired" &&
      subscriptionData?.subscriptionInfo?.isSubscribed &&
      subscriptionData?.subscriptionInfo?.isUnsubscribed &&
      subscriptionData?.subscriptionInfo?.isSubscribedExpired &&
      subscriptionData?.currentPlan?.status !== "Paused"
    ) {
      const expiredValue = "Charge";

      if (
        safeBillingSeats === 0 &&
        isAdditionalSeatsAdd === false
      ) {
        await runUpdate(
          subscriptionData?.id,
          subscriptionDataThatOnlyChangeFromMonthlyToYearly,
          expiredValue
        );
        // Plan-only expired branch always goes Charge — runUpdate captured
        // the response body, fall through to applyChargeResponse below by
        // not returning early. setUpdateSeatsModal stays to preserve the
        // post-success UI flow (the modal will open after Close).
        setUpdateSeatsModal(true);
      } else if (
        isAdditionalSeatsAdd ||
        safeBillingSeats > 0 ||
        safeBillingSeats < 0
      ) {
        await runUpdate(
          subscriptionData?.id,
          updateSubscriptionAndSubscriptionIsExpired,
          expiredValue
        );
        setUpdateSeatsModal(true);
      } else {
        await runUpdate(
          subscriptionData?.id,
          updateSubscriptionThatSubscriptionIsAlreadyExpired,
          expiredValue
        );
      }
    } else if (
      subscriptionData?.userTrial?.isTrialCancelled &&
      subscriptionData?.currentPlan?.plan === "Free" &&
      subscriptionData?.currentPlan?.status === "Free" &&
      !subscriptionData?.subscriptionInfo?.isSubscribed
    ) {
      await runUpdate(
        subscriptionData?.id,
        subscriptionDataToSubscribeAfterCancelTrial,
        value
      );
    } else {
      await runUpdate(subscriptionData?.id, subscriptionDataToSubscribe, value);
    }

    if (!isCharge) {
      // Update flow — refresh state and let the click handler close the modal.
      // No further session/subscription refetch for Charge: that happens in
      // the success transition handler (and App.js's socket handler also
      // refreshes on PAYMENT_PAID).
      try { await getSession(); } catch (_) { /* tolerate transient session refresh failures */ }
      if (user?.company?.id) {
        await getSubscriptionByCompanyId(user?.company?.id);
      }
      localStorage.removeItem("selectedPlan");
    } else {
      // Charge flow — interpret the captured response body. Three terminal
      // outcomes plus the "wait for webhook" hold:
      //   • recovered:true → backend verified a stale PendingPayment was
      //     already paid by Moyasar. No new charge happened. Jump to success.
      //   • error:true → synchronous Moyasar decline (invalid token, network).
      //     Backend wrote lastPaymentFailure and returned a generic message.
      //     Map to failed state with the i18n key if present.
      //   • processing:true + paymentId → Moyasar accepted the charge; the
      //     webhook will deliver PAYMENT_PAID/PAYMENT_FAILED. Set
      //     currentPaymentId + sessionStorage.inFlowPaymentId so the socket
      //     listener can match the event and App.js can dedupe its toast.
      //   • anything else (legacy non-Charge backend path) → treat as success.
      const body = chargeResponseBody;
      if (body && body.recovered === true) {
        writeSuccessDialogFlagSync();
        setPaymentState("success");
      } else if (body && body.error === true) {
        const reasonKey = body.i18nKey || body.message || null;
        writeFailedDialogFlagSync(reasonKey);
        setFailureReasonKey(reasonKey);
        setPaymentState("failed");
      } else if (body && body.processing === true && body.paymentId) {
        setCurrentPaymentId(body.paymentId);
        try { sessionStorage.setItem("inFlowPaymentId", String(body.paymentId)); } catch (_) { /* sessionStorage may be unavailable */ }
        // Stay in 'processing'; socket listener handles transition.
      } else {
        // Unexpected response shape — empty body, missing paymentId on
        // processing:true, or a contract drift. Treat as failed rather than
        // optimistically declaring success — the user must NOT see a
        // "Subscribed successfully" toast for an outcome we can't verify.
        // The 60s timeout would also catch it but surfacing immediately is
        // better UX. Logs the actual body so a contract drift is debuggable.
        // eslint-disable-next-line no-console
        console.warn(
          "[ChargeBillingDetails] Unexpected Charge response shape — treating as failed.",
          body
        );
        writeFailedDialogFlagSync("Payment.TimeoutReason");
        setFailureReasonKey("Payment.TimeoutReason");
        setPaymentState("failed");
      }
    }
    } catch (err) {
      if (isCharge) {
        // HTTP 409 = duplicate-pending guard tripped. The PRIOR attempt is
        // still in flight on Moyasar; its webhook will fire and we'd miss it
        // because our paymentId isn't set. Best UX: stay in processing for
        // the timeout window so the user sees the original attempt's outcome
        // if the prior tab/window is still listening. Otherwise the 60-second
        // timeout will surface a retry-friendly failed state.
        if (err?.response?.status === 409) {
          return;
        }
        const reasonKey =
          err?.response?.data?.i18nKey ||
          err?.response?.data?.lastPaymentFailure?.i18nKey ||
          null;
        const reasonMsg =
          err?.response?.data?.message ||
          err?.message ||
          null;
        const finalReason = reasonKey || reasonMsg;
        writeFailedDialogFlagSync(finalReason);
        setFailureReasonKey(finalReason);
        setPaymentState("failed");
        return;
      }
      // Update flow — re-throw so the click handler's onHide/history.push
      // skip on failure (modal stays open).
      throw err;
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
      if (!isCharge) {
        setShowProcessPaymentLoader(false);
      }
    }
  };

  const handleOutsideClick = (e) => {
    // Outside-click closes only when the user is still in Invoice Preview.
    // For Processing/Failed/Success, the dialog locks the user into the flow
    // — closing must be a deliberate Close button click. This matches the
    // Figma (no header X icon on Processing/Failed/Success either).
    if (e.target.id === "default-modal" && paymentState === "invoice") {
      onHide();
    }
  };

  // ─── handleRetry — Failed → Processing ──────────────────────────────────────
  // Hits the backend's existing /retry-payment endpoint which replays the
  // last failed PendingPayment's requestBody against Moyasar. Reuses the same
  // socket-driven success/failed transition path as the initial Charge.
  const handleRetry = async () => {
    if (!confirmArmed) return;
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);

    setFailureReasonKey(null);
    setCurrentPaymentId(null);
    try { sessionStorage.removeItem("inFlowPaymentId"); } catch (_) { /* sessionStorage may be unavailable */ }
    // Drop the persisted Failed-dialog flag: the user is leaving the
    // Failed view to retry. Without this, if the retry transitions back
    // to Failed, the persist-on-failed useEffect overwrites the flag with
    // the new reason — but a parent remount BEFORE that transition (rare
    // race, but App.js fires getSession on the retry's PAYMENT_FAILED
    // socket event) could resurrect the OLD reason. Clearing here keeps
    // the persisted state strictly in sync with the live state machine.
    try { sessionStorage.removeItem("failedDialogActive"); } catch (_) { /* sessionStorage may be unavailable */ }
    // Drop any stale Processing-restore flag from a PRIOR attempt before this
    // retry starts. A fresh paymentId is written below once /retry-payment
    // returns processing; the opener re-writes processingDialogActive with it.
    try { sessionStorage.removeItem("processingDialogActive"); } catch (_) { /* sessionStorage may be unavailable */ }
    setPaymentState("processing");

    try {
      // The redux retrySubscriptionPayment thunk returns the promise-middleware
      // resolved action. We need the underlying axios response body to extract
      // paymentId — easiest to bypass the thunk for parity with handlePayment's
      // Charge path. Same endpoint as services/subscription.js retrySubscriptionPayment.
      const resp = await axios.post(`/admin/subscriptions/${subscriptionData?.id}/retry-payment`);
      const body = resp?.data || null;
      if (body && body.recovered === true) {
        writeSuccessDialogFlagSync();
        setPaymentState("success");
      } else if (body && body.error === true) {
        const reasonKey = body.i18nKey || body.message || null;
        writeFailedDialogFlagSync(reasonKey);
        setFailureReasonKey(reasonKey);
        setPaymentState("failed");
      } else if (body && body.processing === true && body.paymentId) {
        setCurrentPaymentId(body.paymentId);
        try { sessionStorage.setItem("inFlowPaymentId", String(body.paymentId)); } catch (_) { /* sessionStorage may be unavailable */ }
      } else {
        // No structured response — assume success and let the post-close
        // refetch reveal the real state. Better than getting stuck.
        writeSuccessDialogFlagSync();
        setPaymentState("success");
      }
    } catch (err) {
      if (err?.response?.status === 409) {
        // Another retry is genuinely in flight (or the backend already cleared
        // lastPaymentFailure mid-window). Stay in processing; the processing
        // timeout will surface a retry-friendly failure if no socket event
        // arrives.
        return;
      }
      // Timeout / 5xx gateway errors: the backend's Moyasar call may have
      // timed out client-side while Moyasar still processes the charge and
      // fires the payment_status webhook asynchronously. Going straight to
      // the Failed dialog here would (a) be premature — the charge may
      // actually succeed — and (b) ignore the late webhook, since the
      // socket listener only transitions OUT of "processing". Stay in
      // "processing"; the modal's own processing-timeout bounds the wait
      // and surfaces a retry-friendly failure if nothing arrives. This is
      // the dialog-flow equivalent of the footer's timeout-toast suppression.
      const status = err?.response?.status;
      const errMessage = err?.response?.data?.message || err?.message || "";
      const isTimeoutOrGateway =
        status === 502 || status === 504 ||
        /timeout/i.test(errMessage) || err?.code === "ECONNABORTED";
      if (isTimeoutOrGateway) {
        console.warn(
          "[ChargeBillingDetails handleRetry] timeout/gateway error — staying in processing; webhook or processing-timeout will resolve.",
          { status, errMessage }
        );
        return;
      }
      // Structured `errorCode: 'RETRY_STALE'` from the backend means the
      // failed payment is outside the 14-day retry window. The /retry-payment
      // endpoint can't be used; the user must subscribe normally (the Charge
      // path computes a fresh amount against current plan/seats). Surface a
      // dedicated actionable message instead of the generic decline copy so
      // the user knows what to do next. Bare key — the render path
      // prefixes `Subscriptions.` and runs i18n() with the right language
      // at paint time.
      if (err?.response?.data?.errorCode === "RETRY_STALE") {
        writeFailedDialogFlagSync("RetryTooStale");
        setFailureReasonKey("RetryTooStale");
        setPaymentState("failed");
        return;
      }
      const reasonKey =
        err?.response?.data?.i18nKey ||
        err?.response?.data?.lastPaymentFailure?.i18nKey ||
        null;
      const reasonMsg = err?.response?.data?.message || err?.message || null;
      const finalReason = reasonKey || reasonMsg;
      writeFailedDialogFlagSync(finalReason);
      setFailureReasonKey(finalReason);
      setPaymentState("failed");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  // ─── handleClose — terminal exits from Success and Failed ───────────────────
  // outcome: 'success' | 'failed'
  // Both paths clear in-flow sessionStorage flags. Success fires the
  // "Subscribed successfully" toast and navigates. Failed sets the
  // `inFlowFailureHandled` flag so the parent's "Last payment failed" banner
  // and App.js's late-arriving socket toast both skip the just-dismissed
  // outcome for the current session.
  const handleClose = (outcome) => {
    try { sessionStorage.removeItem("inFlowPaymentId"); } catch (_) { /* sessionStorage may be unavailable */ }
    try { sessionStorage.removeItem("subscriptionIntent"); } catch (_) { /* sessionStorage may be unavailable */ }
    // Terminal cleanup of the Processing-restore survival flag — both Close
    // outcomes (success auto-close + failed Close) and the PAYMENT_PAID
    // auto-close (handleClose("success")) flow through here, so a completed
    // flow can never leave a stale processingDialogActive to re-open a spinner.
    try { sessionStorage.removeItem("processingDialogActive"); } catch (_) { /* sessionStorage may be unavailable */ }
    // Terminal cleanup of the auto-retry intent. The opener useEffect normally
    // consumes `retryDialogIntent` when it opens Processing, but a post-success
    // AppRoutes remount can re-run the verifyCard `fetchCard` path and re-write
    // the flag (e.g. backend leaves a stale tempToken). Clearing it on every
    // terminal exit guarantees no stale "1" survives a completed flow and
    // spuriously re-opens Processing → re-fires /retry-payment on a later visit
    // (double-charge guard).
    try { sessionStorage.removeItem("retryDialogIntent"); } catch (_) { /* sessionStorage may be unavailable */ }
    // Clear the Failed-dialog persistence on every terminal exit (success
    // OR failed). On success the user has resolved the failure; on failed
    // the user explicitly dismissed the dialog and any later remount must
    // honor that dismissal (re-opening would be the regression the ticket
    // describes). Pairs with the inFlowFailureHandled flag set in the
    // outcome==='failed' branch below — both gates work together to keep
    // the dialog closed after Close.
    try { sessionStorage.removeItem("failedDialogActive"); } catch (_) { /* sessionStorage may be unavailable */ }
    // Clear the Success-dialog persistence too. The user has acknowledged
    // the success and is moving on — any subsequent remount must not
    // re-show the Payment Successful modal (would be visible noise after
    // the user already saw the confirmation banner + page refresh).
    try { sessionStorage.removeItem("successDialogActive"); } catch (_) { /* sessionStorage may be unavailable */ }
    if (outcome === "failed") {
      // 5-minute TTL marker so the banner stays suppressed for this attempt
      // but reappears on next session or stale-state reload.
      //
      // For sync failures (Moyasar rejected before issuing a paymentId) the
      // currentPaymentId is null. We still write the marker with a `noPaymentId`
      // flag so the banner check in company-subscription-view can suppress
      // the just-dismissed failure regardless of paymentId match. Without
      // this, sync-failure Close would land on the subscription page with
      // the "Last payment failed" banner immediately visible — direct
      // violation of the plan's "no banner after in-flow Close" requirement.
      try {
        sessionStorage.setItem(
          "inFlowFailureHandled",
          JSON.stringify({
            paymentId: currentPaymentId ? String(currentPaymentId) : null,
            noPaymentId: !currentPaymentId,
            timestamp: Date.now(),
          })
        );
      } catch (_) {
        /* sessionStorage may be unavailable */
      }
    }
    stateMachineActiveRef.current = false;
    if (outcome === "success") {
      // Mark the failure HANDLED on success too (not just on the failed Close).
      // After PAYMENT_PAID the backend clears lastPaymentFailure / flips status
      // to Running asynchronously, so the post-success subscriptionData refetch
      // can briefly still read Paused + the old lastPaymentFailure — which made
      // the opener's cron-failure branch pop a stale "Payment Failed" dialog
      // immediately after the success toast (reported regression). This 5-min
      // marker (same gate the failed-Close uses) suppresses that race window.
      try {
        sessionStorage.setItem(
          "inFlowFailureHandled",
          JSON.stringify({
            paymentId: currentPaymentId ? String(currentPaymentId) : null,
            noPaymentId: !currentPaymentId,
            timestamp: Date.now(),
          })
        );
      } catch (_) {
        /* sessionStorage may be unavailable */
      }
      // Sync-write the persistent banner flag BEFORE the toast / onHide /
      // navigate so the parent's mount-time useState initializer picks it
      // up even if an AppRoutes remount fires before the window event
      // handler attaches. Same sync-then-event coordination as the
      // failed/success dialog flags — write to sessionStorage first, then
      // dispatch so the steady-state path also updates immediately.
      try {
        sessionStorage.setItem(
          "subscribedSuccessfullyBanner",
          JSON.stringify({
            plan: newSelectedPlan || subscriptionData?.currentPlan?.plan || null,
            timestamp: Date.now(),
          })
        );
      } catch (_) {
        /* sessionStorage may be unavailable */
      }
      try {
        window.dispatchEvent(new CustomEvent("subscribedSuccessfullyBannerOpen"));
      } catch (_) {
        /* CustomEvent unavailable in very old browsers / test contexts */
      }
      try {
        SubscriptionCustomToast(i18n("Toast.SubscribeSuccessfully"));
      } catch (_) {
        /* toast unavailable in test contexts */
      }
      onHide();
      history.push("/management/subscriptions");
    } else {
      onHide();
    }
  };

  const isTrialRunning = checkIsTrialRunning(subscriptionData);
  const isTrialCancel = checkIsTrialCancelled(subscriptionData);
  const isSubscriptionRunning = checkIsSubscriptionRunning(subscriptionData);
  const isSubscriptionExpired = checkIsSubscriptionExpired(subscriptionData);
  const isTrialExpired = checkIsTrialExpired(subscriptionData);
  const isTrialSubscriptionRunning = checkIsTrialSubscriptionRunning(subscriptionData);

  // ─── Seat count display helper (unchanged logic, NaN-guarded) ────────────────
  const getSeatCountForDisplay = () => {
    // Trial must come FIRST — lastSubscriptionData.seats holds previously added
    // seat counts (e.g. 1) which is wrong here; we always want the full seat total.
    if (isTrialContext) {
      const trialSeats = safeBillingTotalSeats > 0
        ? safeBillingTotalSeats
        : safeSeatsTotal > 0
          ? safeSeatsTotal
          : safeSeatsUsed;
      return trialSeats;
    }
    // Free plan subscribers charge for seats.used only — Free plan's seats.total
    // is always the default 10 regardless of actual usage, so never display it
    // as the charge seat count. Must run before the safeBillingTotalSeats gate
    // (which gets the default 10 passed in from company-subscription-view).
    if (
      subscriptionData?.currentPlan?.plan === "Free" &&
      subscriptionData?.currentPlan?.status === "Free" &&
      safeBillingSeats <= 0
    ) {
      return safeSeatsUsed;
    }
    // Active-subscription seat upgrade: when the user is on a Running
    // Monthly/Yearly subscription and is adding seats (safeBillingSeats
    // here is the delta, not the new total — UpdatePlan.jsx sets
    // `billingSeats = newTotal - existingTotal`), the Invoice Preview
    // must show the delta so the displayed line
    //   "{seats} seats × SAR {proratedCostPerSeat} = SAR {seatsPrice}"
    // multiplies out correctly. Without this guard the next branch
    // returns `safeBillingTotalSeats` (the full new total) and the
    // arithmetic on screen no longer matches: e.g. "3 seats × SAR 19.50
    // = SAR 19.50" when only 1 seat was added. Same logic applies to
    // Monthly and Yearly per the ticket. Two guards here:
    //   - `isValidSubscription && safeBillingSeats > 0` keeps the May 18
    //     fix path intact for the well-formed case.
    //   - `isActiveSeatUpgrade` is the defense-in-depth path for the
    //     state-race case where parsedBillingDetails / newSelectedPlan
    //     hasn't propagated yet (see the comment on isActiveSeatUpgrade
    //     above). Without this second guard, the next branch
    //     (`safeBillingTotalSeats > 0`) silently returns the total and
    //     the bug returns under refetch / save-race conditions.
    if (isValidSubscription && safeBillingSeats > 0) {
      return safeBillingSeats;
    }
    if (isActiveSeatUpgrade) {
      return safeBillingSeats;
    }
    if (safeBillingTotalSeats > 0) {
      return safeBillingTotalSeats;
    }
    if (subscriptionData?.lastSubscriptionData?.seats && safeBillingSeats <= 0) {
      return subscriptionData.lastSubscriptionData.seats;
    }
    if (subscriptionData?.currentPlan?.plan === "Trial") {
      return safeSeatsTotal;
    }
    if (subscriptionData?.currentPlan?.plan === "Free" && billingSeats === undefined) {
      return safeSeatsUsed;
    }
    if (subscriptionData?.currentPlan?.plan === "Free" && billingSeats !== undefined) {
      return safeBillingSeats;
    }
    if (
      subscriptionData?.currentPlan?.status === "Unsubscribed, subscription is running" &&
      safeBillingSeats < 0
    ) {
      return safeBillingTotalSeats;
    }
    if (
      subscriptionData?.currentPlan?.plan === "Yearly Subscription" &&
      safeBillingSeats > 0 &&
      newSelectedPlan === subscriptionData?.currentPlan?.plan
    ) {
      return safeBillingSeats;
    }
    if (newSelectedPlan !== subscriptionData?.currentPlan?.plan) {
      return safeBillingTotalSeats;
    }
    if (
      parsedBillingDetails?.plan === "Yearly Subscription" &&
      subscriptionData?.currentPlan?.status !== "Expired" &&
      safeBillingSeats < 0
    ) {
      return safeBillingTotalSeats;
    }
    if (
      parsedBillingDetails?.plan === "Yearly Subscription" &&
      subscriptionData?.currentPlan?.status === "Expired" &&
      safeBillingSeats > 0
    ) {
      return safeBillingSeats;
    }
    if (parsedBillingDetails?.seats) {
      return parsedBillingDetails.seats;
    }
    if (
      safeBillingTotalSeats &&
      subscriptionData?.userTrial?.isTrialCancelled &&
      subscriptionData?.currentPlan?.plan === "Free"
    ) {
      return safeBillingTotalSeats;
    }
    if (
      subscriptionData?.userTrial?.isTrialCancelled &&
      subscriptionData?.currentPlan?.plan === "Free" &&
      isAdditionalSeatsAdd === undefined
    ) {
      return safeSeatsUsed;
    }
    if (
      subscriptionData?.currentPlan?.plan !== newSelectedPlan &&
      subscriptionData?.subscriptionInfo?.isSubscribed === true
    ) {
      return safeBillingTotalSeats;
    }
    if (isValidSubscription && safeBillingSeats > 0) {
      return safeBillingSeats;
    } else if (isValidSubscription && safeBillingSeats <= 0) {
      return safeBillingTotalSeats;
    }
    if (
      subscriptionData?.currentPlan?.status === "Expired" ||
      subscriptionData?.currentPlan?.status === "Paused"
    ) {
      if (safeBillingSeats && safeBillingTotalSeats) {
        return safeBillingTotalSeats;
      } else {
        return safeSeatsTotal;
      }
    }
    return 0;
  };

  const seatCountDisplay = getSeatCountForDisplay();

  return (
    <>
      <div
        className={`fixed inset-0 flex items-center justify-center w-full h-full z-50 px-8 ${show ? "block" : "hidden"
          } bg-black bg-opacity-50`}
        onClick={handleOutsideClick}
        id="default-modal"
        tabIndex="-1"
        aria-hidden="true"
      >
        <div className="bg-white md:w-[550px] h-auto rounded-2xl py-2">
          {/*
            ─── State machine render gate ────────────────────────────────────
            Replaces the prior empty-SVG-spinner ternary. The four states
            render mutually exclusive content inside the same modal box (same
            width / border-radius / shadow as before — only the body swaps).
            Figma frames 5-9 (Processing / Failed / Success) all share these
            dimensions. The Invoice Preview state retains the full legacy
            body. None of the state views render a header X icon — Close /
            Retry buttons own the transitions for those three states.
          */}
          {paymentState === "processing" && (
            <div className="p-6 flex flex-col gap-3 min-h-[160px]">
              {/*
                Inline style block — _animations.scss with the keyframes is
                not imported into the JS bundle (styles.scss isn't an entry
                point), so the dots wouldn't animate from the SCSS alone.
                Inline ensures the animation works regardless of bundler
                pipeline. Browsers dedupe identical <style> nodes; multiple
                Processing dialog mounts don't compound.
              */}
              <style>{`
                @keyframes payment-dot-bounce {
                  0%, 60%, 100% { transform: translateY(0); opacity: 0.3; }
                  30%           { transform: translateY(-6px); opacity: 1; }
                }
                .payment-dots {
                  display: inline-flex;
                  align-items: center;
                  gap: 6px;
                }
                .payment-dot {
                  display: inline-block;
                  width: 8px;
                  height: 8px;
                  border-radius: 50%;
                  background-color: #2D9CDB;
                  animation: payment-dot-bounce 1.2s infinite ease-in-out;
                }
                .payment-dot:nth-child(2) { animation-delay: 0.15s; }
                .payment-dot:nth-child(3) { animation-delay: 0.30s; }
              `}</style>
              <p className="text-lg font-bold flex">{i18n("Payment.ProcessingTitle")}</p>
              <p className={`text-sm text-quaternary ${language === "ARABIC" ? "text-right" : "text-left"}`}>
                {i18n("Payment.ProcessingBody")}
              </p>
              <div className="flex justify-center items-center mt-6 mb-2">
                <span className="payment-dots" aria-label="processing" role="status">
                  <span className="payment-dot" />
                  <span className="payment-dot" />
                  <span className="payment-dot" />
                </span>
              </div>
            </div>
          )}
          {paymentState === "failed" && (
            <div className="p-6 flex flex-col gap-3 min-h-[160px]">
              <p className="text-lg font-bold flex">{i18n("Payment.FailedTitle")}</p>
              <p className={`text-sm text-quaternary ${language === "ARABIC" ? "text-right" : "text-left"}`}>
                {i18n("Payment.FailedBody")}
              </p>
              {failureReasonKey && (
                <p className={`text-sm text-quaternary ${language === "ARABIC" ? "text-right" : "text-left"}`}>
                  {i18n("Payment.ReasonPrefix")}:{" "}
                  {(() => {
                    // Render the i18n-mapped reason if the key exists, else
                    // fall back to the raw value (which on failure could be a
                    // Moyasar passthrough string the backend included verbatim).
                    if (typeof failureReasonKey !== "string") return null;
                    // Normalize raw uppercase / SNAKE_CASE strings (e.g.
                    // "DECLINED", "DO_NOT_HONOR") to the matching
                    // PaymentFailure* key so the user sees contextual
                    // guidance instead of the vague raw token. Per ticket
                    // requirement on "Remove reliance on vague messages
                    // such as DECLINED without contextual guidance."
                    const normalized = normalizeFailureReason(failureReasonKey);
                    if (normalized.includes(".")) {
                      const translated = i18n(normalized);
                      return translated && !translated.includes("Default text") ? translated : normalized;
                    }
                    // Compat: keys like "PaymentFailureCardDeclined" without the
                    // "Subscriptions." prefix come from the backend's mapping.
                    // Try both namespaces, fall back to the raw key.
                    const t1 = i18n(`Subscriptions.${normalized}`);
                    if (t1 && !t1.includes("Default text") && !t1.includes("Subscriptions.")) return t1;
                    return normalized;
                  })()}
                </p>
              )}
              <div className={`flex items-center mt-4 ${language === "ARABIC" ? "justify-start flex-row-reverse" : "justify-end"} gap-6`}>
                <span
                  className={`font-bold text-colPrimary cursor-pointer ${confirmArmed ? "" : "opacity-50 pointer-events-none"}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleRetry()}
                >
                  {i18n("Payment.RetryPayment")}
                </span>
                <span
                  className="font-bold text-quaternary cursor-pointer"
                  role="button"
                  tabIndex={0}
                  onClick={() => handleClose("failed")}
                >
                  {i18n("Payment.Close")}
                </span>
              </div>
            </div>
          )}
          {paymentState === "success" && (
            <div className="p-6 flex flex-col gap-3 min-h-[160px]">
              <p className="text-lg font-bold flex">{i18n("Payment.SuccessTitle")}</p>
              <p className={`text-sm text-quaternary ${language === "ARABIC" ? "text-right" : "text-left"}`}>
                {i18n("Payment.SuccessBody")}
              </p>
              <div className={`flex items-center mt-4 ${language === "ARABIC" ? "justify-start" : "justify-end"}`}>
                <span
                  className="font-bold text-colPrimary cursor-pointer"
                  role="button"
                  tabIndex={0}
                  onClick={() => handleClose("success")}
                >
                  {i18n("Payment.Close")}
                </span>
              </div>
            </div>
          )}
          {paymentState === "invoice" && (
            <div className="d-flex flex-column h-100">
              <div className="w-100">
                <div className="p-4">
                  <p className="text-lg font-bold flex">
                    {i18n("Modals.InvoicePreview")}
                  </p>
                  {isTrialRunning || isTrialSubscriptionRunning || isSubscriptionRunning ||
                    isTrialCancel ||
                    isSubscriptionExpired ||
                    isTrialExpired ? <>
                    {(isTrialRunning || isTrialSubscriptionRunning) && (
                      <p className="text-sm text-quaternary mt-2 flex">
                        {i18n("Modals.ConfirmationInTrialRunning")}
                      </p>
                    )}
                    {(isSubscriptionRunning ||
                      isTrialCancel ||
                      isSubscriptionExpired ||
                      isTrialExpired) && (
                        <p className="text-sm text-quaternary mt-2 md:mt-3 flex">
                          {parsedBillingDetails?.plan ===
                            subscriptionData?.currentPlan?.plan &&
                            safeBillingTotalSeats < safeSeatsTotal &&
                            !showSeatAddWarning
                            ? i18n(
                              "Modals.SummaryOfFeesThatWillBeAppliedAtTheStartOfTheNextBillingCycle"
                            )
                            : i18n(
                              "Modals.SummaryOfFeesToBeAppliedUponConfirmation"
                            )}
                        </p>
                      )}
                  </> :
                    <>
                      <p className="text-sm text-quaternary mt-2 md:mt-3 flex">
                        {i18n("Modals.SummaryOfFeesToBeAppliedUponConfirmation")}
                      </p>
                    </>}
                </div>
                <div className="px-4 flex flex-col gap-1">
                  <div
                    className={`flex justify-between ${language === "ARABIC" ? "flex-row-reverse" : "flex-row"
                      }`}
                  >
                    <span className="text-sm text-quaternary">
                      {i18n("Subscriptions.Description")}
                    </span>
                    <span className="text-sm text-quaternary">
                      {i18n("Subscriptions.Amount")}
                    </span>
                  </div>
                  <div>
                    <hr className="mt-1" />
                  </div>
                </div>
                <div className="px-4 flex flex-col gap-1 mt-1">
                  <p className="text-sm flex">
                    {i18n("Table.UsersManagement")}
                  </p>
                  <p className="text-sm text-quaternary flex">
                    (
                    {show &&
                      (newSelectedPlan
                        ? newSelectedPlan
                        : subscriptionData?.lastSubscriptionData?.plan) &&
                      getBillingCycleDates(
                        newSelectedPlan
                          ? newSelectedPlan
                          : subscriptionData?.lastSubscriptionData?.plan,
                        subscriptionData
                      )}
                    )
                  </p>
                  <div className="flex justify-between">
                    <div>
                      <span className="text-sm">
                        {seatCountDisplay}
                      </span>
                      <span className="mx-1 text-sm">
                        <I18n lng="global.seats" />
                      </span>
                      <span>×</span>
                      <span className="mx-1 text-sm">
                        <I18n lng="Subscriptions.SAR" />
                      </span>
                      <span className="text-sm">
                        {(safeNum(parsedBillingDetails?.proratedCostPerSeat, 0) > 0
                          ? safeNum(parsedBillingDetails.proratedCostPerSeat)
                          : proratedCostPerSeat
                        ).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex gap-1">
                      <span className="text-sm">
                        <I18n lng="Subscriptions.SAR" />
                      </span>
                      <span className="text-sm">
                        {parsedBillingDetails?.seatsPrice
                          ? safeNum(parsedBillingDetails.seatsPrice).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                          : safeNum(usedSeatCalculation).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                      </span>
                    </div>
                  </div>
                  <hr className="mt-1" />
                </div>
                {subscriptionData?.applyTax && (
                  <div className="px-4">
                    <div className="flex justify-between mt-2">
                      <div className="text-sm">
                        <p className="flex">
                          {i18n("Subscriptions.TotalBeforTax")}
                        </p>
                        <p className="mt-2">{i18n("Modals.VAT15")}</p>
                      </div>
                      <div>
                        <p className="flex justify-between text-sm gap-1">
                          <span>
                            <I18n lng="Subscriptions.SAR" />
                          </span>
                          {parsedBillingDetails?.seatsPrice
                            ? safeNum(parsedBillingDetails.seatsPrice).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                            : safeNum(usedSeatCalculation).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                        </p>
                        <p className="flex justify-between text-sm gap-1 mt-2">
                          <span>
                            <I18n lng="Subscriptions.SAR" />
                          </span>
                          <span>
                            {parsedBillingDetails?.taxes
                              ? safeNum(parsedBillingDetails.taxes).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })
                              : safeNum(taxOnUsedSeatCalculation).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                          </span>
                        </p>
                      </div>
                    </div>
                    <hr className="mt-2" />
                  </div>
                )}
              </div>
              <div className="px-4 flex justify-between mt-2 text-sm font-bold">
                <p>{i18n("Modals.AmountDue")}</p>
                <p>
                  <span className="mr-2 ml-2">
                    <I18n lng="Subscriptions.SAR" />
                  </span>
                  <span>
                    {parsedBillingDetails?.total
                      ? safeNum(parsedBillingDetails.total).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                      : safeNum(totalAmount).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                  </span>
                </p>
              </div>
              {isTrialRunning || isTrialSubscriptionRunning || isSubscriptionRunning ||
                isTrialCancel ||
                isSubscriptionExpired ||
                isTrialExpired ?
                <>
                  {(isTrialRunning || isTrialSubscriptionRunning) && (
                    <p
                      className={`px-4 text-sm text-quaternary mt-4 flex  ${language === "ARABIC" ? "text-right" : "text-left"
                        }`}
                    >
                      {i18n("Modals.TheFreeTrialEndsOn")}{" "}
                      {moment(subscriptionData?.trialEndedAt).format("MMM DD, YYYY")}{" "}
                      {i18n("Modals.FollowedBy")} <I18n lng="Subscriptions.SAR" />{" "}
                      {parsedBillingDetails?.plan === "Monthly Subscription" ? "79.00" : "790.00"}
                      /
                      {parsedBillingDetails?.plan === "Monthly Subscription"
                        ? i18n("Subscriptions.month")
                        : i18n("Subscriptions.year")}{" "}
                      {i18n("Modals.PerSeatCancelAnytimeAutoChargedEachCycleUntilCanceled")}
                    </p>
                  )}
                  {(isSubscriptionRunning ||
                    isTrialCancel ||
                    isSubscriptionExpired ||
                    isTrialExpired) && (
                      <p
                        style={{
                          paddingLeft: language === "ARABIC" ? "20px" : "25PX",
                          paddingRight: "25px",
                          textAlign: language === "ARABIC" ? "right" : "left",
                        }}
                        className="text-sm text-quaternary !mt-6 flex"
                      >
                        {(parsedBillingDetails?.plan ===
                          subscriptionData?.currentPlan?.plan &&
                          safeBillingTotalSeats > safeSeatsTotal) ||
                          showSeatAddWarning
                          ? `${i18n(
                            "Modals.YouWillBeChargedTheAboveProratedAmountForTheRestOfThisCycleThenSAR"
                          )} ${parsedBillingDetails?.plan === "Monthly Subscription"
                            ? "79.00"
                            : "790.00"
                          }
                      ${parsedBillingDetails?.plan === "Monthly Subscription"
                            ? i18n("Modals.PerSeatMonthlyUntilCanceled")
                            : i18n("Modals.PerSeatYearlyUntilCanceled")
                          }`
                          : parsedBillingDetails?.plan ===
                            subscriptionData?.currentPlan?.plan &&
                            safeBillingTotalSeats < safeSeatsTotal
                            ? `${i18n(
                              "Modals.TheAdjustedInvoiceWillBeGeneratedInTheNextBillingCycleOn"
                            )} ${moment(subscriptionData?.subscriptionEndedAt).format(
                              "MMM DD, YYYY"
                            )}.`
                            : ` ${i18n(
                              "Modals.YouWillBeChargedAtTheStartOfEachBillingCycleUntilYouCancel"
                            )}`}
                      </p>
                    )}
                </> :
                <p
                  style={{
                    paddingLeft: language === "ARABIC" ? "20px" : "25PX",
                    paddingRight: "25px",
                    textAlign: language === "ARABIC" ? "right" : "left",
                  }}
                  className="text-sm text-quaternary !mt-6 flex"
                >
                  {i18n("Modals.YouWillBeChargedAtTheStartOfEachBillingCycleUntilYouCancel")}
                </p>
              }

              <div
                className={`px-4 flex justify-evenly items-center mt-3 md:!mt-6 mb-3 md:mb-0`}
              >
                <div>
                  <Button
                    onClick={onHide}
                    title={i18n("Modals.CANCEL")}
                    variant="none"
                    className="font-bold text-quaternary border-none focus:border-none focus:outline-none"
                  />
                </div>
                <div>
                  <span className="font-bold text-colPrimary cursor-pointer">
                    {(() => {
                      if (
                        subscriptionData?.currentPlan?.status === "Expired" &&
                        // Only fire this branch for actual seat ADDITIONS while expired.
                        // The previous condition `isAdditionalSeatsAdd !== undefined` was
                        // too permissive: UpdatePlan.jsx:1293 always passes the prop as
                        // `totalSeats > currentPlan.seats ? true : false`, so it was ALWAYS
                        // defined (true OR false). For a re-subscribe with no seat change
                        // (totalSeats === currentPlan.seats), the prop was `false` but
                        // still defined → branch fired → handlePayment("Update") sent →
                        // backend's actionType !== 'Charge' path skipped Moyasar entirely
                        // → frontend updated dates but no payment, no invoice. Tightening
                        // to `=== true` lets re-subscribe-without-seat-change fall through
                        // to the line ~1667 branch which correctly sends "Charge".
                        isAdditionalSeatsAdd === true
                      ) {
                        return (
                          <React.Fragment>
                            <div
                              className="text-center"
                              onClick={async () => {
                                await handlePayment("Charge");
                                // stateMachineActiveRef is flipped synchronously inside handlePayment
                                // for Charge value, before the first await. After handlePayment resolves
                                // we're either still in 'processing' (waiting for webhook) or already
                                // transitioned to 'failed' (sync decline). In both cases the in-dialog
                                // Close button on the new view owns the close — bail before onHide/push.
                                if (stateMachineActiveRef.current) return;
                                onHide();
                                history.push("/management/subscriptions");
                              }}
                            >
                              {i18n("Subscriptions.ConfirmForUnsubscribedModal")}
                            </div>
                          </React.Fragment>
                        );
                      }
                      if (
                        (subscriptionData?.currentPlan?.plan === "Trial" &&
                          subscriptionData?.currentPlan?.status === "Running") ||
                        subscriptionData?.currentPlan?.status === "Trial is running"
                      ) {
                        return (
                          <React.Fragment>
                            <div
                              className="text-center"
                              onClick={async () => {
                                await handlePayment("Update");
                                if (user?.company?.id) {
                                  getSubscriptionByCompanyId(user?.company?.id);
                                }
                                getSession();
                                onHide();
                                history.push("/management/subscriptions");
                                // No optimistic success toast — trialEndCronJob
                                // will fire Moyasar at trial end and the outcome
                                // is surfaced via App.js socket handler
                                // (PAYMENT_PAID / PAYMENT_FAILED). A local "subscribed
                                // successfully" here would race with and contradict
                                // that real result.
                              }}
                            >
                              {i18n("Subscriptions.ConfirmForUnsubscribedModal")}
                            </div>
                          </React.Fragment>
                        );
                      }
                      if (fromUsers === true) {
                        return (
                          <React.Fragment>
                            <div
                              className="text-center"
                              onClick={async () => {
                                await handlePayment("Charge");
                                // stateMachineActiveRef is flipped synchronously inside handlePayment
                                // for Charge value, before the first await. After handlePayment resolves
                                // we're either still in 'processing' (waiting for webhook) or already
                                // transitioned to 'failed' (sync decline). In both cases the in-dialog
                                // Close button on the new view owns the close — bail before onHide/push.
                                if (stateMachineActiveRef.current) return;
                                onHide();
                                history.push("/management/subscriptions");
                              }}
                            >
                              {i18n("Subscriptions.ConfirmForUnsubscribedModal")}
                            </div>
                          </React.Fragment>
                        );
                      }
                      if (safeBillingTotalSeats > safeNum(subscriptionData?.currentPlan?.seats, 0)) {
                        return (
                          <React.Fragment>
                            <div
                              className="text-center"
                              onClick={async () => {
                                await handlePayment("Charge");
                                // stateMachineActiveRef is flipped synchronously inside handlePayment
                                // for Charge value, before the first await. After handlePayment resolves
                                // we're either still in 'processing' (waiting for webhook) or already
                                // transitioned to 'failed' (sync decline). In both cases the in-dialog
                                // Close button on the new view owns the close — bail before onHide/push.
                                if (stateMachineActiveRef.current) return;
                                onHide();
                                history.push("/management/subscriptions");
                              }}
                            >
                              {i18n("Subscriptions.ConfirmForUnsubscribedModal")}
                            </div>
                          </React.Fragment>
                        );
                      }
                      if (
                        // currentPlan.plan enum is only Free / Trial / Monthly Subscription /
                        // Yearly Subscription — the string "Unsubscribed, subscription is
                        // running" lives on currentPlan.status. The previous .plan check was
                        // dead code, which silently dropped the Confirm button for users
                        // re-subscribing during the post-cancel grace period.
                        subscriptionData?.currentPlan?.status === "Unsubscribed, subscription is running" &&
                        safeBillingTotalSeats >= safeNum(subscriptionData?.currentPlan?.seats, 0)
                      ) {
                        return (
                          <React.Fragment>
                            <div
                              className="text-center"
                              onClick={async () => {
                                await handlePayment("Charge");
                                // stateMachineActiveRef is flipped synchronously inside handlePayment
                                // for Charge value, before the first await. After handlePayment resolves
                                // we're either still in 'processing' (waiting for webhook) or already
                                // transitioned to 'failed' (sync decline). In both cases the in-dialog
                                // Close button on the new view owns the close — bail before onHide/push.
                                if (stateMachineActiveRef.current) return;
                                onHide();
                                history.push("/management/subscriptions");
                              }}
                            >
                              {i18n("Subscriptions.ConfirmForUnsubscribedModal")}
                            </div>
                          </React.Fragment>
                        );
                      }
                      if (subscriptionData?.currentPlan?.status === "Paused") {
                        return (
                          <React.Fragment>
                            <div
                              className="text-center"
                              onClick={async () => {
                                await handlePayment("Charge");
                                // stateMachineActiveRef is flipped synchronously inside handlePayment
                                // for Charge value, before the first await. After handlePayment resolves
                                // we're either still in 'processing' (waiting for webhook) or already
                                // transitioned to 'failed' (sync decline). In both cases the in-dialog
                                // Close button on the new view owns the close — bail before onHide/push.
                                if (stateMachineActiveRef.current) return;
                                onHide();
                                history.push("/management/subscriptions");
                              }}
                            >
                              {i18n("Subscriptions.ConfirmForUnsubscribedModal")}
                            </div>
                          </React.Fragment>
                        );
                      }
                      if (
                        selectedPlan !== subscriptionData?.currentPlan?.plan &&
                        subscriptionData?.userTrial?.isTrialCancelled === false
                      ) {
                        return (
                          <React.Fragment>
                            <div
                              className="text-center"
                              onClick={async () => {
                                await handlePayment("Charge");
                                if (stateMachineActiveRef.current) return;
                                await getSession();
                                onHide();
                                history.push("/management/subscriptions");
                              }}
                            >
                              {i18n("Subscriptions.ConfirmForUnsubscribedModal")}
                            </div>
                          </React.Fragment>
                        );
                      }
                      if (
                        subscriptionData?.currentPlan?.plan === "Free" &&
                        subscriptionData?.userTrial?.isTrialCancelled === true
                      ) {
                        return (
                          <React.Fragment>
                            <div
                              className="text-center"
                              onClick={async () => {
                                await handlePayment("Charge");
                                // stateMachineActiveRef is flipped synchronously inside handlePayment
                                // for Charge value, before the first await. After handlePayment resolves
                                // we're either still in 'processing' (waiting for webhook) or already
                                // transitioned to 'failed' (sync decline). In both cases the in-dialog
                                // Close button on the new view owns the close — bail before onHide/push.
                                if (stateMachineActiveRef.current) return;
                                onHide();
                                history.push("/management/subscriptions");
                                // Do NOT toast success here — Moyasar may still
                                // decline. App.js socket handler toasts the real
                                // outcome on PAYMENT_PAID / PAYMENT_FAILED webhook.
                              }}
                            >
                              {i18n("Subscriptions.ConfirmForUnsubscribedModal")}
                            </div>
                          </React.Fragment>
                        );
                      }
                      if (
                        safeBillingTotalSeats < safeNum(subscriptionData?.currentPlan?.seats, 0) &&
                        subscriptionData?.currentPlan?.plan !== "Paused"
                      ) {
                        return (
                          <React.Fragment>
                            <div
                              className="text-center"
                              onClick={async () => {
                                await handlePayment("Update");
                                onHide();
                                history.push("/management/subscriptions");
                              }}
                            >
                              {i18n("Subscriptions.ConfirmForUnsubscribedModal")}
                            </div>
                          </React.Fragment>
                        );
                      }
                      if (
                        subscriptionData?.currentPlan?.status === "Expired" &&
                        isAdditionalSeatsAdd === undefined
                      ) {
                        return (
                          <React.Fragment>
                            <div
                              className="text-center"
                              onClick={async () => {
                                await handlePayment("Charge");
                                // stateMachineActiveRef is flipped synchronously inside handlePayment
                                // for Charge value, before the first await. After handlePayment resolves
                                // we're either still in 'processing' (waiting for webhook) or already
                                // transitioned to 'failed' (sync decline). In both cases the in-dialog
                                // Close button on the new view owns the close — bail before onHide/push.
                                if (stateMachineActiveRef.current) return;
                                onHide();
                                history.push("/management/subscriptions");
                                // Do NOT toast success here — Moyasar may still
                                // decline. App.js socket handler toasts the real
                                // outcome on PAYMENT_PAID / PAYMENT_FAILED webhook.
                              }}
                            >
                              {i18n("Subscriptions.ConfirmForUnsubscribedModal")}
                            </div>
                          </React.Fragment>
                        );
                      }
                      if (
                        // isSubscriptionRunning is the called result; the imported
                        // checkIsSubscriptionRunning identifier is the function reference
                        // (always truthy as a JS object), which made the && short-circuit
                        // pointless. Use the local boolean from line 1033.
                        isSubscriptionRunning &&
                        selectedPlan !== subscriptionData?.currentPlan?.plan
                      ) {
                        return (
                          <React.Fragment>
                            <div
                              className="text-center"
                              onClick={async () => {
                                await handlePayment("Charge");
                                // stateMachineActiveRef is flipped synchronously inside handlePayment
                                // for Charge value, before the first await. After handlePayment resolves
                                // we're either still in 'processing' (waiting for webhook) or already
                                // transitioned to 'failed' (sync decline). In both cases the in-dialog
                                // Close button on the new view owns the close — bail before onHide/push.
                                if (stateMachineActiveRef.current) return;
                                onHide();
                                history.push("/management/subscriptions");
                              }}
                            >
                              {i18n("Subscriptions.ConfirmForUnsubscribedModal")}
                            </div>
                          </React.Fragment>
                        );
                      }

                      // Fall-through default — the IIFE above grew to 11 ad-hoc
                      // conditions over time and silently dropped the Confirm
                      // button when none matched (an empty <span> with cursor-pointer
                      // but no clickable element renders, which is what the
                      // "CONFIRM disabled" bug report described). Routing
                      // unmatched states through handlePayment("Charge") is safe
                      // because handlePayment itself has its own state-aware
                      // routing (charge-billing-details.jsx:763+), and the
                      // backend Charge controller (controllers/subscription.js:92-187)
                      // is idempotent against duplicate pending payments.
                      // The console.warn flags any new state shape so we can add
                      // an explicit branch later instead of silently swallowing it.
                      console.warn(
                        "[ChargeBillingDetails] No Confirm-button branch matched; falling back to Charge.",
                        {
                          plan: subscriptionData?.currentPlan?.plan,
                          status: subscriptionData?.currentPlan?.status,
                          isSubscribed: subscriptionData?.subscriptionInfo?.isSubscribed,
                          isUnsubscribed: subscriptionData?.subscriptionInfo?.isUnsubscribed,
                          isSubscribedExpired: subscriptionData?.subscriptionInfo?.isSubscribedExpired,
                          selectedPlan,
                          totalSeats: safeBillingTotalSeats,
                          currentSeats: subscriptionData?.currentPlan?.seats,
                        }
                      );
                      return (
                        <React.Fragment>
                          <div
                            className="text-center"
                            onClick={async () => {
                              await handlePayment("Charge");
                              if (stateMachineActiveRef.current) return;
                              onHide();
                              history.push("/management/subscriptions");
                            }}
                          >
                            {i18n("Subscriptions.ConfirmForUnsubscribedModal")}
                          </div>
                        </React.Fragment>
                      );
                    })()}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {addPaymentModal && (
        <PlanDialog
          showModal={addPaymentModal}
          onClose={() => setAddPaymentModal(false)}
          modalTitle={addPaymentModalData.title}
          text_one={addPaymentModalData.text_one}
          secondaryButtonLabel={addPaymentModalData.closeButtonText}
          primaryButtonLabel={addPaymentModalData.actionButtonText}
          secondaryButtonColor="#828282"
          primaryButtonColor="#20639B"
          titleWidth={"222px"}
          titleHeight={"20px"}
          titleFontWeight={"600"}
          titleFontSize={"20px"}
          titleLineHeight={"20px"}
          titleColor={"#333333"}
          secondaryButtonWidth={"43px"}
          secondaryButtonHeight={"16px"}
          secondaryButtonFontWeight={"600"}
          secondaryButtonFontSize={"16px"}
          secondaryButtonLineHeight={"15.92px"}
          primaryButtonWidth={"152px"}
          primaryButtonHeight={"30px"}
          primaryButtonFontWeight={"600"}
          primaryButtonFontSize={"14px"}
          primaryButtonLineHeight={"29.57px"}
        />
      )}
    </>
  );
};

const mapStateToProps = ({ authentication, subscription }) => ({
  user: authentication.user,
  subscriptionData: subscription.subscriptionData,
  loading: subscription.loading,
});

const mapDispatchToProps = {
  createUser,
  updateSubscriptionData,
  getSubscriptionByCompanyId,
  getSession,
  getAllUsers,
  updateUserById,
  getAllInvoices,
  getInvoices,
  getCompanyById,
  getCard,
};

export default connect(mapStateToProps, mapDispatchToProps)(ChargeBillingDetails);